import React, { useState, useEffect, useRef } from 'react';
import { Switch, FormControlLabel } from '@mui/material';
import PaginacionControl from '../components/PaginacionControl';
import debounce from 'lodash.debounce';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, TextField, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Chip, Alert, CircularProgress, useTheme, useMediaQuery, Tabs, Tab, Popover
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { saveAs } from 'file-saver';
import Autocomplete from '@mui/material/Autocomplete';
import { Add, Delete, Edit, AttachFile, CloudUpload, CheckCircle, Cancel, Receipt, Gavel, Warning, Refresh, Description, Archive, Close } from '@mui/icons-material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import WhatsAppDialog from '../components/WhatsAppDialog';
import EmailDialog from '../components/EmailDialog';
import EmailIcon from '@mui/icons-material/Email';
import { GridOn } from '@mui/icons-material';
import axios from 'axios';
import Checkbox from '@mui/material/Checkbox';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { exportFacturasWorkbook } from '../utils/excel/facturaWorkbook';
import ClienteRapidoDialog from '../components/ClienteRapidoDialog';
import ProductoRapidoDialog from '../components/ProductoRapidoDialog';
import { useResizableCols } from '../hooks/useResizableCols';
import ProductoBuscadorDialog, { StockAlmacenesDialog } from '../components/ProductoBuscadorDialog';
import { Search as SearchIcon } from '@mui/icons-material';

// Orígenes de referencia que ya movieron kardex al crearse (guía, nota de venta) — a diferencia
// de cotización, que nunca afecta stock. La cantidad de estas líneas no debe editarse en el
// comprobante porque ya se descontó una cantidad exacta en el almacén.
const ORIGENES_YA_AFECTAN_KARDEX = ['guia_remision', 'nota_venta'];

const COL_DEFAULTS_FACTURA = {
  codigo:   80,
  producto: 220,
  cant:     60,
  umedida:  75,
  precio:   85,
  dscto:    70,
  total:    85,
  almacen:  120,
  acciones: 85,
};

const ResizeHandleFactura = ({ onMouseDown }) => {
  const [hover, setHover] = useState(false);
  return (
    <span
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: 8,
        cursor: 'col-resize', userSelect: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <span style={{
        width: hover ? 3 : 2, height: hover ? '70%' : '55%', borderRadius: 1,
        background: hover ? 'rgba(25,118,210,0.7)' : 'rgba(0,0,0,0.22)',
        pointerEvents: 'none', transition: 'all 0.12s',
      }} />
    </span>
  );
};

const initialDetalle = {
  producto_id: '',
  codigo_producto: '',
  descripcion_producto: '',
  cantidad: '',
  unidad_medida: '',
  precio_unitario: 0,
  descuento_monto: 0,
  descuento_porcentaje: 0,
  subtotal: 0,
  tipo_igv: '10',
  igv_monto: 0,
  total: 0,
  observaciones: ''
};

const getAdjuntoStartIcon = (nombre = '') => {
  const filename = String(nombre || '').toLowerCase();
  const isXml = filename.endsWith('.xml');
  const isCdr = filename.includes('cdr') || filename.startsWith('r-') || filename.endsWith('.zip');

  if (isCdr) return <CheckCircle />;
  if (isXml) return <Receipt />;
  return <AttachFile />;
};

const getAdjuntosArray = (rawAdjuntos) => {
  if (Array.isArray(rawAdjuntos)) return rawAdjuntos;
  if (typeof rawAdjuntos === 'string' && rawAdjuntos.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(rawAdjuntos);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const findAdjuntoByTipo = (rawAdjuntos, tipo) => {
  const adjuntos = getAdjuntosArray(rawAdjuntos);
  return adjuntos.find((name) => {
    const filename = String(name || '').toLowerCase();
    const isCdr = filename.includes('cdr') || filename.startsWith('r-') || filename.endsWith('.zip');
    const isXml = filename.endsWith('.xml') && !isCdr;
    return tipo === 'cdr' ? isCdr : isXml;
  });
};

const FacturaPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { widths: colW, startResize } = useResizableCols('factura-detail-cols', COL_DEFAULTS_FACTURA);
    const [openBuscadorProducto, setOpenBuscadorProducto] = useState(false);
    const [openStockAlmacen, setOpenStockAlmacen] = useState(false);
    const [copiarNombreADesc, setCopiarNombreADesc] = useState(true);
    // Estado para distinguir entre edición y nuevo
    const [modoEdicion, setModoEdicion] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [tiposIGV, setTiposIGV] = useState([]);
    const [loadingTiposIGV, setLoadingTiposIGV] = useState(false);
    
    // Estado para el diálogo de datos adicionales
    const [openDatosAdicionales, setOpenDatosAdicionales] = useState(false);

    // Estado para el diálogo de ANULACIÓN ELECTRÓNICA
    const [openAnulacion, setOpenAnulacion] = useState(false);
    const [facturaAAnular, setFacturaAAnular] = useState(null);
    const [motivoAnulacion, setMotivoAnulacion] = useState('');
    const [procesandoAnulacion, setProcesandoAnulacion] = useState(false);
    const [openPdfDialog, setOpenPdfDialog] = useState(false);
    const [facturaPdfTarget, setFacturaPdfTarget] = useState(null);
    const [wspDialog, setWspDialog] = useState({ open: false, data: null });
    const [emailDialog, setEmailDialog] = useState({ open: false, data: null });
    const [pdfDescargando, setPdfDescargando] = useState(false);
    const [pdfFormatoSeleccionado, setPdfFormatoSeleccionado] = useState(() => localStorage.getItem('factura_pdf_formato_default') || 'A4');
    const [guardarPdfComoPredeterminado, setGuardarPdfComoPredeterminado] = useState(true);
    const [pdfResolucion, setPdfResolucion] = useState({});
    const [pdfResolucionCargando, setPdfResolucionCargando] = useState(false);

    // Estado y función para almacenes
    const [almacenes, setAlmacenes] = useState([]);
    const [almacenInput, setAlmacenInput] = useState('');
    const [loadingAlmacenes, setLoadingAlmacenes] = useState(false);

    // Función para cargar almacenes
    const fetchAlmacenes = async (query = '') => {
      setLoadingAlmacenes(true);
      try {
        const res = await axios.get('/api/almacen/mis-almacenes', { params: { q: query } });
        setAlmacenes(res.data);
        if (res.data.length === 1) {
          setDetalle(prev => ({ ...prev, almacen_id: res.data[0].id }));
        }
      } catch (err) {
        setAlmacenes([]);
      }
      setLoadingAlmacenes(false);
    };

    // Debounce para evitar muchas llamadas
    const debouncedFetchAlmacenes = React.useMemo(() => debounce(fetchAlmacenes, 300), []);

    // Función para abrir el diálogo de datos adicionales
    const handleOpenDatosAdicionales = async () => {
      const facturaId = form.id;
      // Solo cargar desde API si el id cambió (nuevo documento o diferente al último cargado)
      if (facturaId && facturaId !== datosCargadosParaId.current) {
        try {
          const [resPlaca, resOC, resGuias] = await Promise.all([
            axios.get(`/api/datos-adicionales/FACTURA/${facturaId}/placa`).catch(() => null),
            axios.get(`/api/datos-adicionales/FACTURA/${facturaId}/orden_compra`).catch(() => null),
            axios.get(`/api/comprobante-guia-refs/${facturaId}`).catch(() => null)
          ]);
          setPlacaAdicional(resPlaca?.data?.valor || '');
          setOrdenCompraAdicional(resOC?.data?.valor || '');
          setGuiasReferencia((resGuias?.data || []).map(r => r.guia_serie_numero));
          datosCargadosParaId.current = facturaId;
        } catch {
          setPlacaAdicional('');
          setOrdenCompraAdicional('');
          setGuiasReferencia([]);
        }
      }
      // Si ya fue cargado (mismo id) o es nuevo (sin id), conservar lo que el usuario escribió
      setGuiaInput('');
      setOpenDatosAdicionales(true);
    };
    // Función para cargar tipos IGV (debe ir antes del useEffect)
    const fetchTiposIGV = async () => {
    setLoadingTiposIGV(true);
    try {
        const res = await axios.get('/api/tipo-igv');
        setTiposIGV(res.data);
    } catch (err) {
        setTiposIGV([]);
    }
    setLoadingTiposIGV(false);
    };
    // Función para cargar condiciones de pago (debe ir antes del useEffect)
    const fetchCondicionesPago = async () => {
      setLoadingCondicionesPago(true);
      try {
        const res = await axios.get('/api/condicion-pago');
        setCondicionesPago(res.data);
        // Autoseleccionar Contado si existe
        const contado = res.data.find(c => c.descripcion.toLowerCase() === 'contado');
        if (contado) {
          setForm(f => ({ ...f, condicion_pago: contado.id }));
        }
      } catch (err) {
        setCondicionesPago([]);
      }
      setLoadingCondicionesPago(false);
    };
    const [facturas, setFacturas] = useState([]);
    const [open, setOpen] = useState(false);
    const [verFactura, setVerFactura] = useState(null);
    const [form, setForm] = useState({
        serie: '',
        numero: '',
        fecha_emision: new Date().toLocaleDateString('en-CA'),
        fecha_validez: '',
        cliente_id: '',
        vendedor_id: '',
        moneda: 'PEN',
        tipo_cambio: 1,
        condicion_pago: 'CONTADO',
        cuotas_json: '',
        subtotal: 0,
        descuento_global: 0,
        porcentaje_descuento_global: 0,
        total_igv: 0,
        total: 0,
        observaciones: '',
        estado: 'PENDIENTE',
        detalles: [],
        adjuntos: [],
        sucursal_id: '',
        es_anticipo: false,
        anticipo_referencia_id: null,
        total_anticipos: 0,
        detraccion_aplicada: false,
        detraccion_monto: 0,
        detraccion_cuenta: '',
        detraccion_porcentaje_personalizado: false,
        detraccion_porcentaje: 12,
        retencion_aplicada: false,
        retencion_monto: 0,
    });
    const [detalle, setDetalle] = useState(initialDetalle);
    const [editingDetalle, setEditingDetalle] = useState(null);
    const [clientes, setClientes] = useState([]);
    const [clienteInput, setClienteInput] = useState('');
    const [loadingClientes, setLoadingClientes] = useState(false);
    const [openNuevoCliente, setOpenNuevoCliente] = useState(false);
    const [openNuevoProducto, setOpenNuevoProducto] = useState(false);
    const [productoInput, setProductoInput] = useState('');
    // Solo mostrar el usuario logueado como vendedor
    const [vendedores, setVendedores] = useState([]);
    const [productos, setProductos] = useState([]);
    const [monedas, setMonedas] = useState([]);
    const [condicionesPago, setCondicionesPago] = useState([]);
    const [loadingCondicionesPago, setLoadingCondicionesPago] = useState(false);
    const [monedaInput, setMonedaInput] = useState('');
    const [loadingMonedas, setLoadingMonedas] = useState(false);
    const [adjuntos, setAdjuntos] = useState([]);
    const [series, setSeries] = useState([]);
    const [serieInput, setSerieInput] = useState('');
    const [loadingSeries, setLoadingSeries] = useState(false);
    const [sucursales, setSucursales] = useState([]);
    const [sucursalInput, setSucursalInput] = useState('');
    const [loadingSucursales, setLoadingSucursales] = useState(false);
    const [unidades, setUnidades] = useState([]);
    const [detalleError, setDetalleError] = useState({ producto: false, cantidad: false, unidad_medida: false });
    const [detalleErrorMsg, setDetalleErrorMsg] = useState('');
    const [stockDisponible, setStockDisponible] = useState(null);
    const [preciosIncluyenIgv, setPreciosIncluyenIgv] = useState(false);
    // Estados para el modal de cotizaciones
    const [openCotizaciones, setOpenCotizaciones] = useState(false);
    const [cotizacionesDisponibles, setCotizacionesDisponibles] = useState([]);
    const [cotizacionesSeleccionadas, setCotizacionesSeleccionadas] = useState([]);
    // Filtro universal de búsqueda
    const [filtroCotizacion, setFiltroCotizacion] = useState('');
    const [vistaCotizacion, setVistaCotizacion] = useState(null);
    const [filtroCotizacionInput, setFiltroCotizacionInput] = useState('');
    // Paginado para cotizaciones
    const [cotizacionesPage, setCotizacionesPage] = useState(0);
    const [cotizacionesRowsPerPage, setCotizacionesRowsPerPage] = useState(4);
    // Tipo de referencia para importar detalles
    const [tipoReferencia, setTipoReferencia] = useState('cotizacion');
    // Pestañas del popup de referencias: 0 = Disponibles, 1 = Ya referenciado
    const [refTab, setRefTab] = useState(0);
    const [refInfoAnchor, setRefInfoAnchor] = useState(null);
    const [refInfoTexto, setRefInfoTexto] = useState('');

    // Estados para búsqueda de comprobante anticipo de referencia
    const [openAnticipoSearch, setOpenAnticipoSearch] = useState(false);
    const [anticiposDisponibles, setAnticipiosDisponibles] = useState([]);
    const [filtroAnticipo, setFiltroAnticipo] = useState('');
    const [anticipNombre, setAnticipNombre] = useState('');

    // Estado para búsqueda
    const [search, setSearch] = useState("");
    const [isRefreshingList, setIsRefreshingList] = useState(false);
    const [lastRefreshAt, setLastRefreshAt] = useState(null);
    const [reenviandoCpeId, setReenviandoCpeId] = useState(null);

    // Obtener el usuario logueado (ejemplo: desde localStorage o contexto)
    const usuarioLogueado = JSON.parse(localStorage.getItem('usuario'));

    // Buscar tipo de cambio al cambiar la fecha de emisión si la moneda es USD
    const handleFechaEmisionChange = async (e) => {
        const nuevaFecha = e.target.value;
        setForm(prevForm => {
        const nuevoForm = { ...prevForm, fecha_emision: nuevaFecha };
        // Si la moneda ya es USD, consultar tipo de cambio
        if (nuevoForm.moneda === 'USD') {
            consultarTipoCambio(nuevaFecha);
        }
        return nuevoForm;
        });
    };

  // Función auxiliar para consultar tipo de cambio
  const consultarTipoCambio = async (fecha) => {
    try {
      const resBD = await axios.get('/api/tipo-cambio');
      const tc = resBD.data.find(x => x.fecha && x.fecha.substring(0,10) === fecha);
      if (tc && tc.venta) {
        setForm(f => ({ ...f, tipo_cambio: tc.venta }));
        return;
      }
    } catch {}
    try {
      const res = await axios.get(`/api/tipo-cambio/sunat?fecha=${fecha}`);
      if (res.data && res.data.venta) {
        setForm(f => ({ ...f, tipo_cambio: res.data.venta }));
        return;
      }
    } catch {}
    setForm(f => ({ ...f, tipo_cambio: 1 }));
  };

  console.log('usuarioLogueado:', usuarioLogueado);

  const puedeReenviarCpe = (nv) => {
    const estadoCpe = String(nv?.estado_cpe || '').toUpperCase();
    if (nv?.estado === 'ANULADA' || Number(nv?.estado_registro) === 0) return false;
    // Rechazado o anulado: no se reenvía, hay que emitir un comprobante nuevo
    if (estadoCpe === 'RECHAZADO' || estadoCpe === 'ANULADO') return false;
    // Permitir en estados fallidos/pendientes sin encolar
    return (
      estadoCpe === 'PENDIENTE' ||
      estadoCpe === 'REINTENTAR' ||
      estadoCpe === 'ERROR_REINTENTO' ||
      estadoCpe.startsWith('ERROR_')
    );
  };

  const reenviarCpe = async (nv) => {
    try {
      setReenviandoCpeId(nv.id);
      const usuario = usuarioLogueado ? (usuarioLogueado.username || usuarioLogueado.email || usuarioLogueado.usuario || 'api') : 'api';

      const resEncolar = await axios.post(`/api/comprobante/${nv.id}/encolar-envio`, { usuario, force: true });
      const yaActivo = !!resEncolar.data?.alreadyActive;
      const forzado = !!resEncolar.data?.forcedNow;
      const yaFinal = !!resEncolar.data?.alreadyFinal;

      if (yaFinal) {
        await fetchFacturas();
        alert(`Este comprobante ya fue enviado y resuelto ante SUNAT (estado: ${resEncolar.data?.estadoActual || 'ENVIADO'}). No se reenviará.`);
        return;
      }

      // Intentar procesar de inmediato para feedback rápido; si falla, el scheduler automático lo tomará.
      try {
        await axios.post('/api/comprobante/envio-jobs/procesar-siguiente');
      } catch (_) {
        // Silencioso: el scheduler corre cada pocos segundos.
      }

      await fetchFacturas();

      if (yaActivo && forzado) {
        alert('El comprobante ya tenía un job activo, pero se forzó su reintento inmediato.');
      } else if (yaActivo) {
        alert('Este comprobante ya tenía un job activo de envío. Se seguirá procesando automáticamente.');
      } else {
        alert('Comprobante reencolado para reenvío a SUNAT.');
      }
    } catch (err) {
      alert('No se pudo reencolar el comprobante: ' + (err.response?.data?.error || err.response?.data?.detalle || err.message));
    } finally {
      setReenviandoCpeId(null);
    }
  };

    // Función para cargar referencias (cotizaciones, notas de venta o guías)
    const fetchCotizaciones = async () => {
      const query = filtroCotizacion.trim();
      try {
        const params = { tipo: tipoReferencia };
        if (query) params.search = query;
        if (form.cliente_id) params.cliente_id = form.cliente_id;
        const res = await axios.get('/api/comprobante/referencias', { params });
        let referencias = res.data || [];
        // Marcar cada documento: ¿alguna de sus líneas ya fue referenciada por
        // otro documento (Guía/NV/Factura/Boleta)? — calculado por el backend
        // vía saldo_cantidad (o, en guías, por EXISTS, que no tienen saldo).
        referencias = referencias.map(ref => {
          const dets = Array.isArray(ref.detalles) ? ref.detalles : [];
          const refsLinea = dets.map(d => d.referenciado_por).filter(Boolean);
          const tieneReferencia = dets.some(d =>
            d.referenciado_por || (d.saldo_cantidad != null && Number(d.saldo_cantidad) < Number(d.cantidad) - 0.0001)
          );
          const referenciasResumen = [...new Set(refsLinea.flatMap(r => r.split(' | ')))].join(', ');
          return { ...ref, _tieneReferencia: tieneReferencia, _referenciasResumen: referenciasResumen };
        });
        // Separar por pestaña (Disponibles / Ya referenciado) y luego paginar
        referencias = referencias.filter(r => (refTab === 1) === r._tieneReferencia);
        const start = cotizacionesPage * cotizacionesRowsPerPage;
        const end = start + cotizacionesRowsPerPage;
        referencias = referencias.slice(start, end);
        setCotizacionesDisponibles(referencias);
      } catch (e) {
        setCotizacionesDisponibles([]);
      }
    };

    // Al abrir el modal, limpiar selección y filtrar cotizaciones disponibles
    useEffect(() => {
    if (openCotizaciones) {
        setCotizacionesSeleccionadas([]);
          const cotizacionesJaladas = form.detalles.map(d => d.cotizacion_id); // Filtrar cotizaciones que ya están en el detalle
          if (form.cliente_id) { // Si hay cliente, filtra por su número de documento
            const cliente = clientes.find(c => c.id === form.cliente_id);
            if (cliente && cliente.numero_documento) {
              setFiltroCotizacion(cliente.numero_documento);
              setFiltroCotizacionInput(cliente.numero_documento);
            }
          }
          setCotizacionesPage(0);
          setRefTab(0);
          fetchCotizaciones(); // Siempre llama para mostrar el listado
    }
    }, [openCotizaciones]);

    // Al cambiar filtro, tipo, pestaña o paginado, recargar referencias
    useEffect(() => {
    if (openCotizaciones) fetchCotizaciones();
    }, [filtroCotizacion, cotizacionesPage, cotizacionesRowsPerPage, tipoReferencia, refTab]);

    // Al cambiar el tipo de referencia (Cotización/NV/Guía), volver a la pestaña "Disponibles"
    useEffect(() => { if (openCotizaciones) { setRefTab(0); setCotizacionesPage(0); } }, [tipoReferencia]);

    // Buscar comprobantes de tipo anticipo para referenciar
    const fetchAnticipos = async () => {
      try {
        const res = await axios.get('/api/comprobante', {
          params: { search: filtroAnticipo, tipo_comprobante: '01' }
        });
        const soloAnticipos = (res.data || []).filter(r => r.es_anticipo === 1 || r.es_anticipo === true);
        setAnticipiosDisponibles(soloAnticipos);
      } catch {
        setAnticipiosDisponibles([]);
      }
    };

    useEffect(() => {
      if (openAnticipoSearch) fetchAnticipos();
    }, [openAnticipoSearch, filtroAnticipo]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setFiltroCotizacion(filtroCotizacionInput);
            setCotizacionesPage(0); // Reinicia paginado al buscar
        }, 300);
        return () => clearTimeout(handler);
    }, [filtroCotizacionInput]);

    useEffect(() => {
        fetchTiposIGV();
        fetchFacturas();
        fetchProductos();
        fetchCondicionesPago();
        fetchUnidades();
        fetchClientesAsync("");
        setModoEdicion(false); // Desactivar modo edición en carga inicial
    }, []);

    useEffect(() => {
            fetchFacturas(); 
        }, [search]);
    
    useEffect(() => {
        if (open) {
        fetchVendedoresAsync();
        fetchAlmacenes();
        }
    }, [open]);
    // Búsqueda asíncrona de almacenes en el Autocomplete
        useEffect(() => {
          if (almacenInput !== '') {
            debouncedFetchAlmacenes(almacenInput);
          } else if (open) {
            fetchAlmacenes();
          }
        }, [almacenInput, open]);

    const fetchUnidades = async () => {
        try {
        const res = await axios.get('/api/unidades');
        setUnidades(res.data);
        } catch (err) {
        setUnidades([]);
        }
    };

  const fetchFacturas = async () => {
    setIsRefreshingList(true);
    try {
      const res = await axios.get('/api/comprobante', { params: { search, tipo_comprobante: '01' } });
      setFacturas(res.data);
      setLastRefreshAt(new Date());
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setFacturas([]); // Si no hay facturas, mostrar tabla vacía sin error
        setLastRefreshAt(new Date());
      } else {
        console.error('Error al obtener las facturas:', err);
        if (window && window.console) window.console.log('FACTURA ERROR:', err);
        alert('Error al obtener las facturas: ' + (err.response?.data?.error || err.message));
      }
    } finally {
      setIsRefreshingList(false);
    }
  };

  const formatLastRefresh = (fecha) => {
    if (!fecha) return 'Sin sincronizar';
    return fecha.toLocaleTimeString('es-PE', { hour12: false });
  };
  // Búsqueda asíncrona de clientes
  const fetchClienteGenRef = React.useRef(0);
  const fetchClientesAsync = async (query = '') => {
    const gen = ++fetchClienteGenRef.current;
    setLoadingClientes(true);
    try {
      const res = await axios.get('/api/auxiliares', {
        params: { tipo: 'CLIENTE', q: query }
      });
      if (gen === fetchClienteGenRef.current) setClientes(res.data);
    } catch (err) {
      console.error('Error al buscar clientes:', err);
      if (window && window.console) window.console.log('CLIENTE ERROR:', err);
      alert('Error al buscar clientes: ' + (err.response?.data?.error || err.message));
      if (gen === fetchClienteGenRef.current) setClientes([]);
    }
    if (gen === fetchClienteGenRef.current) setLoadingClientes(false);
  };

  // Debounce para evitar muchas llamadas
  const debouncedFetchClientes = React.useMemo(() => debounce(fetchClientesAsync, 300), []);
  // Ref para evitar re-búsqueda al auto-seleccionar cliente creado
  const skipClienteSearch = React.useRef(false);
  const fetchVendedores = async () => {
    try {
      const res = await axios.get('/api/usuarios');
      setVendedores(res.data);
    } catch (err) {
      console.error('Error al obtener vendedores:', err);
      if (window && window.console) window.console.log('VENDEDOR ERROR:', err);
      alert('Error al obtener vendedores: ' + (err.response?.data?.error || err.message));
    }
  };
  const fetchProductos = async (q = '') => {
    try {
      const params = {};
      if (form.sucursal_id) params.sucursal_id = form.sucursal_id;
      if (q) { params.search = q; params.limit = 60; }
      else if (form.sucursal_id) { params.limit = 5000; }
      const res = await axios.get('/api/productos', { params });
      setProductos(res.data);
    } catch (err) {
      console.error('Error al obtener productos:', err);
      alert('Error al obtener productos: ' + (err.response?.data?.error || err.message));
    }
  };
  const debouncedFetchProductos = React.useMemo(() => debounce(fetchProductos, 300), [form.sucursal_id]);

  // Re-cargar productos filtrados cuando cambia la sucursal seleccionada
  useEffect(() => {
    if (form.sucursal_id) fetchProductos();
  }, [form.sucursal_id]);

  useEffect(() => {
    const productoId = detalle.producto_id;
    const almacenId  = detalle.almacen_id;
    if (!productoId || !almacenId) { setStockDisponible(null); return; }
    const prod = productos.find(p => p.id === productoId);
    if (!prod || !prod.afecta_kardex) { setStockDisponible(null); return; }
    let cancelled = false;
    axios.get(`/api/kardex/stock/${productoId}/${almacenId}`)
      .then(res => { if (!cancelled) setStockDisponible(res.data.stock ?? 0); })
      .catch(() => { if (!cancelled) setStockDisponible(null); });
    return () => { cancelled = true; };
  }, [detalle.producto_id, detalle.almacen_id, productos]);

  const fetchMonedasAsync = async (query = '') => {
    setLoadingMonedas(true);
    try {
      const res = await axios.get('/api/monedas', {
        params: { q: query }
      });
      setMonedas(res.data);
    } catch (err) {
      console.error('Error al buscar monedas:', err);
      setMonedas([]);
    }
    setLoadingMonedas(false);
  };

  const debouncedFetchMonedas = React.useMemo(() => debounce(fetchMonedasAsync, 300), []);

  const fetchSucursalesAsync = async (query = '') => {
    setLoadingSucursales(true);
    try {
      const res = await axios.get('/api/sucursales/mis-sucursales', { params: { q: query } });
      setSucursales(res.data);
      // Seleccionar la primera sucursal por defecto solo en carga inicial (sin filtro)
      if (query === '' && res.data.length > 0) {
        setForm(f => ({ ...f, sucursal_id: f.sucursal_id || res.data[0].id }));
      }
    } catch (err) {
      console.error('Error al buscar sucursales:', err);
      setSucursales([]);
    }
    setLoadingSucursales(false);
  };

  const debouncedFetchSucursales = React.useMemo(() => debounce(fetchSucursalesAsync, 300), []);

  useEffect(() => {
    if (open) {
      fetchSucursalesAsync(''); // cargar inicial
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (sucursalInput !== '') {
      debouncedFetchSucursales(sucursalInput);
    } else {
      fetchSucursalesAsync('');
    }
  }, [sucursalInput]);

  // Filtrar series por sucursal y permisos SOLO tipo NTV (Factura)
  const fetchSeriesPorSucursalYUsuario = async (idSucursal, idUsuario) => {
    setLoadingSeries(true);
    try {
      // Series de la sucursal
      const resSeries = await axios.get(`/api/series/sucursal/${idSucursal}`);
      // Series permitidas para el usuario
      const resPermisos = await axios.get(`/api/usuario-serie/${idUsuario}`);
      const seriesPermitidas = resSeries.data.filter(serie =>
        serie.tipo_documento === '01' &&
        resPermisos.data.some(perm => perm.id === serie.id && perm.activo)
      );
      setSeries(seriesPermitidas);
      // Seleccionar la primera serie por defecto SOLO si NO está en modo edición
      if (!modoEdicion) {
        if (seriesPermitidas.length > 0) {
          setForm(f => ({ ...f, serie: seriesPermitidas[0].serie, numero: seriesPermitidas[0].correlativo_actual }));
        } else {
          setForm(f => ({ ...f, serie: '', numero: '' }));
        }
      }
    } catch (err) {
      console.error('Error al buscar series:', err);
      setSeries([]);
    }
    setLoadingSeries(false);
  };

  // Cuando cambia sucursal, cargar series filtradas por usuario
  useEffect(() => {
    if (form.sucursal_id && form.vendedor_id) {
      fetchSeriesPorSucursalYUsuario(form.sucursal_id, form.vendedor_id);
      // Al cargar series y correlativo, solo actualizar correlativo si NO está en modo edición
      if (!modoEdicion && series.length > 0) {
        // Setear correlativo automáticamente usando la primera serie permitida
        setForm(prev => ({
          ...prev,
          serie: series[0].nombre || series[0].codigo || '',
          numero: series[0].correlativo || '',
        }));
      }
      // Si está en modo edición, NO actualizar el correlativo
    }
  }, [form.sucursal_id, form.vendedor_id]);

  const handleOpen = async () => {
    setModoEdicion(false);
    setAdjuntos([]);
    setGuiasReferencia([]);
    setGuiaInput('');
    setTipoReferencia('cotizacion');
    setFiltroCotizacionInput('');
    setFiltroCotizacion('');
    setCotizacionesSeleccionadas([]);
    // Forzar recarga de vendedores antes de abrir el formulario
    await fetchVendedoresAsync(); // Usar fetchVendedoresAsync
    // Obtener configuración de empresa para el valor por defecto de preciosIncluyenIgv
    try {
      const { obtenerEmpresaConfig } = await import('../utils/empresaConfig');
      const config = await obtenerEmpresaConfig();
      if (config && (config.precios_incluyen_igv === 1 || config.precios_incluyen_igv === true)) {
        setPreciosIncluyenIgv(true);
      } else {
        setPreciosIncluyenIgv(false);
      }
    } catch (err) {
      setPreciosIncluyenIgv(false);
    }
    // Seleccionar automáticamente el vendedor logueado
    if (usuarioLogueado) {
      setForm(f => ({
        ...f,
        id: null,
        detalles: [],
        adjuntos: [],
        fecha_emision: new Date().toLocaleDateString('en-CA'),
        fecha_validez: '',
        vendedor_id: usuarioLogueado.id,
        cliente_id: '', // Limpiar cliente al abrir nueva cotización
        moneda: 'PEN', // No arrastrar moneda de un comprobante editado previamente
        tipo_cambio: 1,
        condicion_pago: (condicionesPago.find(c => c.descripcion?.toLowerCase() === 'contado') || {}).id || '',
        subtotal: 0,
        total_igv: 0,
        total: 0,
        serie: '',   // Limpiar serie
        numero: '',  // Limpiar correlativo
        observaciones: '', // Limpiar observaciones
        es_anticipo: false,
        anticipo_referencia_id: null,
        total_anticipos: 0,
        detraccion_aplicada: false,
        detraccion_monto: 0,
        detraccion_cuenta: '',
        detraccion_porcentaje_personalizado: false,
        detraccion_porcentaje: 12,
        retencion_aplicada: false,
        retencion_monto: 0,
      }));
    } else {
      setForm(f => ({
        ...f,
        id: null,
        detalles: [],
        adjuntos: [],
        fecha_emision: new Date().toLocaleDateString('en-CA'),
        fecha_validez: '',
        vendedor_id: '',
        cliente_id: '', // Limpiar cliente al abrir nueva cotización
        moneda: 'PEN', // No arrastrar moneda de un comprobante editado previamente
        tipo_cambio: 1,
        condicion_pago: (condicionesPago.find(c => c.descripcion?.toLowerCase() === 'contado') || {}).id || '',
        subtotal: 0,
        total_igv: 0,
        total: 0,
        serie: '',   // Limpiar serie
        numero: '',  // Limpiar correlativo
        observaciones: '', // Limpiar observaciones
        es_anticipo: false,
        anticipo_referencia_id: null,
        total_anticipos: 0,
        detraccion_aplicada: false,
        detraccion_monto: 0,
        detraccion_cuenta: '',
        detraccion_porcentaje_personalizado: false,
        detraccion_porcentaje: 12,
        retencion_aplicada: false,
        retencion_monto: 0,
      }));
    }

    // Esperar un ciclo para asegurar que el form se limpió antes de asignar la serie
    setTimeout(() => {
      fetchSeriesPorSucursalYUsuario(form.sucursal_id, form.vendedor_id);
      if (!modoEdicion && series.length > 0) {
        // Setear correlativo automáticamente usando la primera serie permitida
        setForm(prev => ({
          ...prev,
          serie: series[0].nombre || series[0].codigo || '',
          numero: series[0].correlativo || '',
        }));
      }
    }, 0);
    //setModoEdicion(true); // Activar modo edición al abrir para editar
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setForm({ ...form, detalles: [], adjuntos: [], es_anticipo: false, anticipo_referencia_id: null, total_anticipos: 0, detraccion_aplicada: false, detraccion_monto: 0, detraccion_cuenta: '', detraccion_porcentaje_personalizado: false, detraccion_porcentaje: 12, retencion_aplicada: false, retencion_monto: 0, cuotas_json: '' });
    setDetalle(initialDetalle);
    setEditingDetalle(null);
    setModoEdicion(false);
    setPlacaAdicional('');
    setOrdenCompraAdicional('');
    datosCargadosParaId.current = null;
    setAnticipNombre('');
    setCuotas([]);
    setNumeroCuotas(3);
    setFechaCuota('');
    setMontoCuota(0);
    setMostrarFinanciero(false);
    setGuiasReferencia([]);
    setGuiaInput('');
    setTipoReferencia('cotizacion');
    setFiltroCotizacionInput('');
    setFiltroCotizacion('');
    setCotizacionesSeleccionadas([]);
  };

  // Función para detectar si la condición de pago es crédito
  const esCredito = () => {
    const condPago = condicionesPago.find(c => c.id === form.condicion_pago);
    if (!condPago) return false;
    // Usar el campo 'tipo' de la BD para detectar si es crédito
    return (condPago.tipo || '').toLowerCase() === 'credito';
  };

  // Obtener dias de crédito de la condición de pago seleccionada
  const obtenerDiasCredito = () => {
    const condPago = condicionesPago.find(c => c.id === form.condicion_pago);
    return condPago && condPago.dias_credito ? parseInt(condPago.dias_credito) : 30;
  };

  // Calcular cuotas OPCIÓN A: 1 sola cuota en dias_credito, pero divisible en múltiples si user quiere
  const calcularCuotasOpcionA = (numeroDivisiones = 1) => {
    if (!form.total || numeroDivisiones <= 0) return;
    const diasCredito = obtenerDiasCredito();
    // Monto neto pendiente de pago: no incluye retención ni detracción (el cliente
    // no paga esa parte directamente) — mismo cálculo que se usa al grabar.
    const montoNeto = Number(form.total)
      - (form.retencion_aplicada ? Number(form.retencion_monto || 0) : 0)
      - (form.detraccion_aplicada ? Number(form.detraccion_monto || 0) : 0);
    const montoXCuota = montoNeto / Number(numeroDivisiones);
    const nuevasCuotas = [];
    const fechaEmision = new Date(form.fecha_emision);
    
    for (let i = 0; i < numeroDivisiones; i++) {
      const fechaVenc = new Date(fechaEmision);
      // Distribuir los días entre las cuotas
      const diasPorCuota = Math.ceil((diasCredito / numeroDivisiones) * (i + 1));
      fechaVenc.setDate(fechaVenc.getDate() + diasPorCuota);
      nuevasCuotas.push({
        numero: i + 1,
        fecha: fechaVenc.toISOString().substring(0, 10),
        monto: montoXCuota
      });
    }
    setCuotas(nuevasCuotas);
    setForm(f => ({ ...f, cuotas_json: JSON.stringify(nuevasCuotas) }));
  };

  // useEffect para gestionar cuotas cuando cambia la condición de pago
  useEffect(() => {
    if (!esCredito()) {
      setCuotas([]);
      setNumeroCuotas(0);
      setForm(f => ({ ...f, cuotas_json: '' }));
    } else if (cuotas.length === 0 && Number(form.total) > 0) {
      // Al seleccionar crédito con total disponible, auto-generar 1 cuota predeterminada
      calcularCuotasOpcionA(1);
      setNumeroCuotas(1);
    }
  }, [form.condicion_pago, open, condicionesPago]); // eslint-disable-line

  // Recalcular la cuota única automática si cambia retención/detracción DESPUÉS de
  // haberse generado (ej. usuario marca crédito primero y retención después) —
  // solo mientras siga siendo la cuota por defecto (1 sola, sin dividir manualmente).
  useEffect(() => {
    if (esCredito() && numeroCuotas === 1 && cuotas.length === 1) {
      calcularCuotasOpcionA(1);
    }
  }, [form.retencion_aplicada, form.retencion_monto, form.detraccion_aplicada, form.detraccion_monto]); // eslint-disable-line

  const agregarCuotaManual = () => {
    if (!fechaCuota || Number(montoCuota) <= 0) {
      alert('Ingresa una fecha y monto válidos');
      return;
    }
    const nuevaCuota = {
      numero: cuotas.length + 1,
      fecha: fechaCuota,
      monto: Number(montoCuota)
    };
    const nuevosCuotas = [...cuotas, nuevaCuota];
    setCuotas(nuevosCuotas);
    setForm(f => ({ ...f, cuotas_json: JSON.stringify(nuevosCuotas) }));
    setFechaCuota('');
    setMontoCuota(0);
  };

  // Recalcular cuota automática cuando cambia el total (sólo si hay exactamente 1 cuota auto-generada)
  useEffect(() => {
    if (esCredito() && numeroCuotas === 1 && Number(form.total) > 0) {
      calcularCuotasOpcionA(1);
    }
  }, [form.total]); // eslint-disable-line

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDetalleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'producto_id') {
      const prod = productos.find(p => p.id === value);
      setDetalle({
        ...detalle,
        producto_id: value,
        codigo_producto: prod?.codigo || '',
        descripcion_producto: copiarNombreADesc ? (prod?.nombre || '') : '',
        unidad_medida: prod && prod.unidad_medida_id ? prod.unidad_medida_id : '',
        precio_unitario: prod && prod.precio != null ? prod.precio : detalle.precio_unitario
      });
    } else {
      setDetalle({ ...detalle, [name]: value });
    }
    setDetalleError({ producto: false, cantidad: false, unidad_medida: false, almacen: false });
    setDetalleErrorMsg('');
  };

  const handleBuscadorSelect = (prod) => {
    setProductoInput(prod?.nombre || '');
    setDetalle(d => ({
      ...d,
      producto_id: prod.id,
      codigo_producto: prod.codigo || '',
      descripcion_producto: copiarNombreADesc ? (prod.nombre || '') : d.descripcion_producto,
      precio_unitario: prod.precio != null ? prod.precio : d.precio_unitario,
      unidad_medida: prod.unidad_medida_id ? prod.unidad_medida_id : d.unidad_medida,
    }));
    setDetalleError(e => ({ ...e, producto: false }));
    setDetalleErrorMsg('');
  };

  const calcularDetalle = (d) => {
    const cantidad = parseFloat(d.cantidad) || 0;
    const precio = parseFloat(d.precio_unitario) || 0;
    const desc = parseFloat(d.descuento_monto) || 0;
    const descP = parseFloat(d.descuento_porcentaje) || 0;
    const tasaIgv = 0.18;
    const tipoIGV = Array.isArray(tiposIGV) ? tiposIGV.find(t => t.codigo === d.tipo_igv) : undefined;
    // Usar d.tipo_igv directo como fallback cuando tiposIGV no está cargado
    const codigoIgv = tipoIGV?.codigo || d.tipo_igv || '10';
    let subtotal = 0, igv = 0, total = 0;
    // Códigos gratuitos según tabla SUNAT
    const codigosGratuitos = ['21', '31', '32', '33', '34', '35'];
    const esGratuito = codigosGratuitos.includes(codigoIgv);
    // Códigos gravados
    const codigosGravados = ['10','11','12','13','14','15','16','17'];
    if (esGratuito) {
      subtotal = Math.round((precio * cantidad - desc - (precio * cantidad * descP / 100)) * 10000) / 10000;
      igv = 0;
      total = 0;
    } else if (preciosIncluyenIgv && codigosGravados.includes(codigoIgv)) {
      const precioFinal = precio * cantidad - desc - (precio * cantidad * descP / 100);
      subtotal = Math.round((precioFinal / (1 + tasaIgv)) * 10000) / 10000;
      igv = Math.round((precioFinal - subtotal) * 10000) / 10000;
      total = Math.round(precioFinal * 10000) / 10000;
    } else {
      subtotal = Math.round((precio * cantidad - desc - (precio * cantidad * descP / 100)) * 10000) / 10000;
      if (codigosGravados.includes(codigoIgv)) {
        igv = Math.round(subtotal * tasaIgv * 10000) / 10000;
      } else {
        igv = 0;
      }
      total = Math.round((subtotal + igv) * 10000) / 10000;
    }
    return { ...d, subtotal, igv_monto: igv, total };
  };
  // Estado para mostrar/ocultar el resumen por tipo de IGV
  const [mostrarResumenIGV, setMostrarResumenIGV] = useState(false);
  // Estado para mostrar/ocultar opciones financieras (Anticipo, Detracción, Retención, Cuotas)
  const [mostrarFinanciero, setMostrarFinanciero] = useState(false);
  // Estado para cuotas de pago
  const [cuotas, setCuotas] = useState([]);
  const [numeroCuotas, setNumeroCuotas] = useState(0);
  const [fechaCuota, setFechaCuota] = useState('');
  const [montoCuota, setMontoCuota] = useState(0);

  const agregarDetalle = () => {
    let error = false;
    let errorObj = { producto: false, cantidad: false, unidad_medida: false };
    let errorMsg = '';
    if (!detalle.producto_id) {
      error = true;
      errorObj.producto = true;
      errorMsg = 'Debes seleccionar un producto.';
    }
    if (!detalle.cantidad || isNaN(detalle.cantidad) || Number(detalle.cantidad) <= 0) {
      error = true;
      errorObj.cantidad = true;
      errorMsg = errorMsg ? 'Selecciona un producto y una cantidad mayor a 0.' : 'La cantidad debe ser mayor a 0.';
    }
    if (!detalle.unidad_medida) {
      error = true;
      errorObj.unidad_medida = true;
      errorMsg = errorMsg ? 'Selecciona producto, cantidad y unidad de medida.' : 'Debes seleccionar una unidad de medida.';
    }
    if (!detalle.almacen_id) {
      error = true;
      errorObj.almacen = true;
      errorMsg = errorMsg ? 'Selecciona producto, cantidad, unidad de medida y almacén.' : 'Debes seleccionar un almacén.';
    }
    if (error) {
      setDetalleError(errorObj);
      setDetalleErrorMsg(errorMsg);
      return;
    }
    if (stockDisponible !== null) {
      const cantEnGrid = form.detalles
        .filter(d => d.producto_id === detalle.producto_id && d.almacen_id === detalle.almacen_id)
        .reduce((s, d) => s + Number(d.cantidad || 0), 0);
      if (cantEnGrid + Number(detalle.cantidad) > Number(stockDisponible)) {
        setDetalleError(e => ({ ...e, cantidad: true }));
        setDetalleErrorMsg(cantEnGrid > 0
          ? `Stock insuficiente. Ya tiene ${cantEnGrid.toFixed(2)} de este producto en la grilla. Disponible: ${parseFloat(stockDisponible).toFixed(2)} unidades.`
          : `Stock insuficiente. Disponible en almacén: ${parseFloat(stockDisponible).toFixed(2)} unidades.`);
        return;
      }
    }
    // Si la descripción está vacía, usar el nombre del producto seleccionado
    let descripcionFinal = detalle.descripcion_producto;
    if (!descripcionFinal) {
      const prod = productos.find(p => p.id === detalle.producto_id);
      descripcionFinal = prod && prod.nombre ? prod.nombre : '';
    }
    // Obtener objeto de unidad de medida
    const unidadObj = unidades.find(u => u.id === detalle.unidad_medida);
    const almacenObj = almacenes.find(a => a.id === detalle.almacen_id);

    const det = calcularDetalle({
      ...detalle,
      descripcion_producto: descripcionFinal,
      unidad_medida_id: unidadObj ? unidadObj.id : null,
      unidad_medida: unidadObj ? unidadObj.abreviatura : '',
      almacen_id: almacenObj ? almacenObj.id : ''
    });
    setForm({ ...form, detalles: [...form.detalles, det] });
    setDetalle(almacenes.length === 1 ? { ...initialDetalle, almacen_id: almacenes[0].id } : initialDetalle);
    setProductoInput('');
    fetchProductos();
    setEditingDetalle(null);
    setStockDisponible(null);
    setDetalleError({ producto: false, cantidad: false, unidad_medida: false, almacen: false });
    setDetalleErrorMsg('');
  };

  const editarDetalle = (idx) => {
    const d = form.detalles[idx];
    let unidadObj = null;
    if (d.unidad_medida_id) {
      unidadObj = unidades.find(u => u.id === d.unidad_medida_id);
    } else if (d.unidad_medida) {
      unidadObj = unidades.find(u => u.abreviatura === d.unidad_medida || u.id === d.unidad_medida);
    }
    setDetalle({
      ...d,
      unidad_medida: unidadObj ? unidadObj.id : '',
      unidad_medida_id: unidadObj ? unidadObj.id : null
    });
    setProductoInput(productos.find(p => p.id === d.producto_id)?.nombre || '');
    setEditingDetalle(idx);
  };

  const actualizarDetalle = () => {
    if (!detalle.almacen_id) {
      setDetalleError(e => ({ ...e, almacen: true }));
      setDetalleErrorMsg('Debes seleccionar un almacén.');
      return;
    }
    // Items de guía o nota de venta ya afectaron kardex — no volver a validar stock
    const yaAfectoKardex = ORIGENES_YA_AFECTAN_KARDEX.includes(detalle.origen_tipo);
    if (!yaAfectoKardex && stockDisponible !== null) {
      const cantEnGrid = form.detalles
        .filter((d, idx) => d.producto_id === detalle.producto_id && d.almacen_id === detalle.almacen_id && idx !== editingDetalle)
        .reduce((s, d) => s + Number(d.cantidad || 0), 0);
      if (cantEnGrid + Number(detalle.cantidad) > Number(stockDisponible)) {
        setDetalleError(e => ({ ...e, cantidad: true }));
        setDetalleErrorMsg(cantEnGrid > 0
          ? `Stock insuficiente. Otras filas ya usan ${cantEnGrid.toFixed(2)} unidades. Disponible: ${parseFloat(stockDisponible).toFixed(2)} unidades.`
          : `Stock insuficiente. Disponible en almacén: ${parseFloat(stockDisponible).toFixed(2)} unidades.`);
        return;
      }
    }
    setDetalleError({ producto: false, cantidad: false, unidad_medida: false, almacen: false });
    setDetalleErrorMsg('');
    let descripcionFinal = detalle.descripcion_producto;
    if (!descripcionFinal) {
      const prod = productos.find(p => p.id === detalle.producto_id);
      descripcionFinal = prod && prod.nombre ? prod.nombre : '';
    }
    const unidadObj = unidades.find(u => u.id === detalle.unidad_medida);
    const almacenObj = almacenes.find(a => a.id === detalle.almacen_id);
    const det = calcularDetalle({
      ...detalle,
      descripcion_producto: descripcionFinal,
      unidad_medida_id: unidadObj ? unidadObj.id : null,
      unidad_medida: unidadObj ? unidadObj.abreviatura : '',
      almacen_id: almacenObj ? almacenObj.id : '',
    });
    setForm({ ...form, detalles: form.detalles.map((d, idx) => idx === editingDetalle ? det : d) });
    setDetalle(almacenes.length === 1 ? { ...initialDetalle, almacen_id: almacenes[0].id } : initialDetalle);
    setProductoInput('');
    fetchProductos();
    setStockDisponible(null);
    setEditingDetalle(null);
  };

  const eliminarDetalle = (idx) => {
    const nuevos = form.detalles.filter((_, i) => i !== idx);
    setForm({ ...form, detalles: nuevos });
  };

  const handleAdjunto = (e) => {
    const files = Array.from(e.target.files);
    setAdjuntos([...adjuntos, ...files]);
    setForm({ ...form, adjuntos: [...form.adjuntos, ...files] });
  };

  const calcularTotales = () => {
    // Definir códigos según tabla SUNAT
    const codigosGravados = ['10','11','12','13','14','15','16','17'];
    const codigosExonerados = ['20','21'];
    const codigosInafectos = ['30','31','32','33','34','35','36'];
    const codigosExportacion = ['40'];
    const codigosGratuitos = ['21','31','32','33','34','35'];

    let subtotal = 0, total_igv = 0, total = 0;
    let total_gravada = 0, total_exonerada = 0, total_inafecta = 0, total_gratuita = 0, total_exportacion = 0;
    let total_otros_cargos = 0, total_icbper = 0, total_anticipos = 0, total_percepcion = 0, total_otros_impuestos = 0;

    form.detalles.forEach(d => {
      const tipoIGV = Array.isArray(tiposIGV) ? tiposIGV.find(t => t.codigo === d.tipo_igv) : undefined;
      // Fallback a d.tipo_igv directo cuando tiposIGV aun no cargo (evita que
      // total_gravada quede en 0 mientras total_igv/total si se calculan bien).
      const cod = tipoIGV?.codigo || d.tipo_igv;
      const montoTotal = parseFloat(d.total) || 0;
      const montoSubtotal = parseFloat(d.subtotal) || 0;
      // Sumar por tipo (base imponible = subtotal sin IGV)
      if (codigosGravados.includes(cod)) total_gravada += montoSubtotal;
      if (codigosExonerados.includes(cod)) total_exonerada += montoSubtotal;
      if (codigosInafectos.includes(cod)) total_inafecta += montoSubtotal;
      if (codigosExportacion.includes(cod)) total_exportacion += montoSubtotal;
      if (codigosGratuitos.includes(cod)) total_gratuita += montoSubtotal;
      // Sumar totales generales (solo si NO es gratuito)
      if (!codigosGratuitos.includes(cod)) {
        subtotal += montoSubtotal;
        total_igv += parseFloat(d.igv_monto) || 0;
        total += montoTotal;
      }
    });
    setForm(f => ({
      ...f,
      subtotal,
      total_igv,
      total,
      total_gravada,
      total_exonerada,
      total_inafecta,
      total_gratuita,
      total_exportacion,
      total_otros_cargos,
      total_icbper,
      total_anticipos,
      total_percepcion,
      total_otros_impuestos
    }));
  };

  useEffect(() => {
    if (form.detalles.length > 0) calcularTotales();
  }, [form.detalles]);

  const handleSubmit = async () => {
    if (guardando) return; // Evita doble envío por doble clic mientras se está grabando
    setGuardando(true);
    try {
    if (!form.detalles || form.detalles.length === 0) {
      alert('Debe agregar al menos un producto o servicio al detalle antes de guardar.');
      return;
    }
    const itemsSinProducto = form.detalles.filter(d => !d.producto_id);
    if (itemsSinProducto.length > 0) {
      const descripciones = itemsSinProducto.map(d => d.descripcion_producto || d.descripcion || '(sin descripción)').join(', ');
      alert(`Falta seleccionar un producto para: ${descripciones}. Esto suele pasar al jalar ítems de una guía que no tenía un producto asignado — selecciona uno del buscador antes de guardar.`);
      return;
    }
    // Cliente marcado como RUC obligatorio para Factura (SUNAT no acepta DNI/otro documento)
    const clienteSel = clientes.find(c => c.id === form.cliente_id);
    if (clienteSel && clienteSel.tipo_documento_identidad_id !== '6') {
      alert(`No se puede grabar la Factura: el cliente "${clienteSel.razon_social || ''}" no tiene RUC (tiene ${clienteSel.tipo_documento_desc || 'otro documento'}). Las facturas electrónicas solo pueden emitirse a clientes con RUC — usa una Boleta en su lugar.`);
      return;
    }
    // Aviso: cliente marcado como agente de retención/detracción sin habilitarla en esta factura
    if (clienteSel?.agente_retencion && !form.retencion_aplicada) {
      if (!window.confirm(`El cliente "${clienteSel.razon_social}" está registrado como Agente de Retención.\n\nNo has habilitado la retención en esta factura. ¿Deseas continuar sin aplicarla?`)) {
        return;
      }
    }
    if (clienteSel?.agente_detraccion && !form.detraccion_aplicada) {
      if (!window.confirm(`El cliente "${clienteSel.razon_social}" está registrado como sujeto a Detracción.\n\nNo has habilitado la detracción en esta factura. ¿Deseas continuar sin aplicarla?`)) {
        return;
      }
    }
    // Subir adjuntos al backend antes de guardar cotización
    let adjuntos_json = '[]';
    if (adjuntos.length > 0) {
      const formData = new FormData();
      formData.append('serie', form.serie);
      formData.append('numero', form.numero);
      adjuntos.forEach(f => formData.append('adjuntos', f));
      try {
        const res = await axios.post('/api/comprobante/upload-adjuntos', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data.success) {
          adjuntos_json = JSON.stringify(res.data.files);
        }
      } catch (err) {
        alert('Error al subir adjuntos: ' + (err.response?.data?.error || err.message));
        return;
      }
    }
    // Obtener objeto de condición de pago seleccionado
    const detallesConAlmacen = form.detalles.map(d => {
      // Buscar siempre el objeto de unidad de medida por id o abreviatura
      let unidadObj = null;
      if (d.unidad_medida_id) {
        unidadObj = unidades.find(u => u.id === d.unidad_medida_id);
      } else if (d.unidad_medida) {
        unidadObj = unidades.find(u => u.abreviatura === d.unidad_medida || u.id === d.unidad_medida);
      }
      return {
        ...d,
        almacen_id: d.almacen_id || form.almacen_id,
        unidad_medida_id: unidadObj ? unidadObj.id : null,
        unidad_medida: unidadObj ? unidadObj.abreviatura : ''
      };
    });
    const condicionPagoObj = condicionesPago.find(c => c.id === form.condicion_pago);

    // ── Cuotas de crédito ─────────────────────────────────────────────────────
    // Monto neto pendiente de pago: no incluye retencion ni detraccion (confirmado
    // con SUNAT beta). El total del comprobante sigue completo, esto es solo para
    // las cuotas/FormaPago que efectivamente le pagara el cliente.
    const montoNetoCuotas = Number(form.total)
      - (form.retencion_aplicada ? Number(form.retencion_monto || 0) : 0)
      - (form.detraccion_aplicada ? Number(form.detraccion_monto || 0) : 0);
    let cuotasJsonFinal = form.cuotas_json;
    if (esCredito()) {
      let cuotasArr = [];
      try { cuotasArr = JSON.parse(form.cuotas_json || '[]'); } catch (_) { cuotasArr = []; }

      if (cuotasArr.length === 0) {
        // Auto-generar 1 cuota con el monto neto y la fecha de vencimiento calculada
        const diasCredito = obtenerDiasCredito();
        const fechaVenc = new Date(form.fecha_emision);
        fechaVenc.setDate(fechaVenc.getDate() + diasCredito);
        cuotasArr = [{ numero: 1, fecha: fechaVenc.toISOString().substring(0, 10), monto: montoNetoCuotas }];
        cuotasJsonFinal = JSON.stringify(cuotasArr);
        setCuotas(cuotasArr);
        setNumeroCuotas(1);
      } else {
        // Validar que las cuotas sumen el monto neto pendiente de pago
        const totalCuotas = cuotasArr.reduce((sum, c) => sum + Number(c.monto), 0);
        const diff = Math.abs(totalCuotas - montoNetoCuotas);
        if (diff > 0.01) {
          alert(`Las cuotas suman S/ ${totalCuotas.toFixed(2)} pero el monto neto pendiente de pago (total menos retención/detracción) es S/ ${montoNetoCuotas.toFixed(2)}.\nAjusta los montos de las cuotas antes de grabar.`);
          return;
        }
        cuotasJsonFinal = form.cuotas_json;
      }
    } else {
      cuotasJsonFinal = '';
    }

    const data = {
      ...form,
      detalles: detallesConAlmacen,
      cuotas_json: cuotasJsonFinal,
      fecha_validez: form.fecha_validez && !isNaN(new Date(form.fecha_validez)) ? form.fecha_validez : null,
      adjuntos_json,
      condicion_pago_id: condicionPagoObj ? condicionPagoObj.id : null,
      condicion_pago: condicionPagoObj ? condicionPagoObj.descripcion : '',
      total_gravada: form.total_gravada,
      total_exonerada: form.total_exonerada,
      total_inafecta: form.total_inafecta,
      total_gratuita: form.total_gratuita,
      total_exportacion: form.total_exportacion,
      total_otros_cargos: form.total_otros_cargos,
      total_icbper: form.total_icbper,
      total_anticipos: form.total_anticipos,
      total_percepcion: form.total_percepcion,
      total_otros_impuestos: form.total_otros_impuestos,
      usuario_creacion: usuarioLogueado ? (usuarioLogueado.username || usuarioLogueado.email || usuarioLogueado.usuario || '') : '',
      usuario_modificacion: usuarioLogueado ? (usuarioLogueado.username || usuarioLogueado.email || usuarioLogueado.usuario || '') : ''
    };
    // Al crear (no editar), no enviar el número: el "siguiente número" mostrado en
    // el formulario es solo referencial y puede quedar desactualizado (dos facturas
    // creadas sin refrescar la pantalla). Dejar que el backend lo asigne de
    // forma atómica evita duplicados.
    if (!form.id) delete data.numero;
    // Log para verificar los datos enviados
    console.log('Datos enviados a backend (factura):', data);
    // Si existe id, actualizar; si no, crear
    let facturasId = form.id;
    try {
      if (form.id) {
        await axios.put(`/api/comprobante/${form.id}`, { ...data, tipo_comprobante: '01' });
      } else {
        const res = await axios.post('/api/comprobante', { ...data, tipo_comprobante: '01' });
        facturasId = res.data.id || res.data.insertId;
      }
    } catch (err) {
      const d = err.response?.data;
      if (d?.tipo === 'CORRELATIVO_DUPLICADO') {
        setCorrelativoError(d);
        return;
      }
      alert(d?.detalle || d?.error || 'Error al guardar la factura');
      return;
    }
    // Guardar datos adicionales
    const _userAdic = usuarioLogueado ? (usuarioLogueado.username || usuarioLogueado.email || usuarioLogueado.usuario || '') : '';
    if (facturasId) {
      const adicSave = [];
      if (ordenCompraAdicional.trim() !== '') adicSave.push(axios.post('/api/datos-adicionales', { comprobante_tipo: 'FACTURA', comprobante_id: facturasId, campo: 'orden_compra', valor: ordenCompraAdicional.trim(), usuario_creacion: _userAdic }));
      if (placaAdicional.trim() !== '') adicSave.push(axios.post('/api/datos-adicionales', { comprobante_tipo: 'FACTURA', comprobante_id: facturasId, campo: 'placa', valor: placaAdicional.trim(), usuario_creacion: _userAdic }));
      if (form.detraccion_aplicada && form.detraccion_porcentaje_personalizado) adicSave.push(axios.post('/api/datos-adicionales', { comprobante_tipo: 'FACTURA', comprobante_id: facturasId, campo: 'detraccion_porcentaje', valor: String(form.detraccion_porcentaje), usuario_creacion: _userAdic }));
      adicSave.push(axios.post(`/api/comprobante-guia-refs/${facturasId}`, { guias: guiasReferencia, usuario_creacion: _userAdic }));
      await Promise.all(adicSave);
    }
    fetchFacturas();
    handleClose();
    } finally {
      setGuardando(false);
    }
  };
  // Estado y handlers para datos adicionales
  const [placaAdicional, setPlacaAdicional] = useState('');
  const [ordenCompraAdicional, setOrdenCompraAdicional] = useState('');
  const [guiasReferencia, setGuiasReferencia] = useState([]);
  const [guiaInput, setGuiaInput] = useState('');
  const datosCargadosParaId = useRef(null);
  const [correlativoError,  setCorrelativoError]  = useState(null);
  const [fixingCorrelativo, setFixingCorrelativo] = useState(false);

  const handleFixCorrelativoFactura = async () => {
    if (!correlativoError) return;
    setFixingCorrelativo(true);
    try {
      const r = await axios.put('/api/series/fix-correlativo', {
        tipo_documento: correlativoError.tipo_documento,
        serie: correlativoError.serie,
      });
      setForm(f => ({ ...f, numero: r.data.siguiente }));
      setSeries(prev => prev.map(s =>
        s.serie === correlativoError.serie
          ? { ...s, correlativo_actual: r.data.siguiente }
          : s
      ));
      setCorrelativoError(null);
    } catch (e) {
      alert('No se pudo regularizar la serie: ' + (e.response?.data?.error || e.message));
      setCorrelativoError(null);
    } finally {
      setFixingCorrelativo(false);
    }
  };

  // Ventana popup de datos adicionales
  const handleCloseDatosAdicionales = () => setOpenDatosAdicionales(false);

  const estadoVencimiento = (fecha_validez) => {
    if (!fecha_validez) return '';
    const hoy = new Date();
    const venc = new Date(fecha_validez);
    if (venc < hoy) return 'VENCIDA';
    if ((venc - hoy) / (1000 * 60 * 60 * 24) <= 3) return 'POR VENCER';
    return 'VIGENTE';
  };

  const getCpeStatusMeta = (estadoCpe) => {
    const color = {
      'PENDIENTE': 'info',
      'PROCESANDO': 'info',
      'PREPARADO': 'warning',
      'ENVIADO': 'warning',
      'ACEPTADO': 'success',
      'RECHAZADO': 'error',
      'ANULADO': 'error'
    }[estadoCpe] || 'default';

    const label = {
      'PENDIENTE': '⏳ Pendiente',
      'PROCESANDO': '⚙️ Procesando',
      'PREPARADO': '📋 Preparado',
      'ENVIADO': '📤 Enviado a SUNAT',
      'ACEPTADO': '✓ Aceptado',
      'RECHAZADO': '✗ Rechazado',
      'ANULADO': '🗑️ Anulado'
    }[estadoCpe] || estadoCpe;

    return { color, label };
  };

  const getAnulacionStatusMeta = (estadoAnulacion) => {
    const color = {
      'PENDIENTE': 'info',
      'PROCESANDO': 'warning',
      'PENDIENTE_SUNAT': 'warning',
      'ERROR_CONSULTA': 'warning',
      'ACEPTADA': 'success',
      'RECHAZADA': 'error',
      'ERROR_ENVIO': 'error'
    }[estadoAnulacion] || 'default';

    const label = {
      'PENDIENTE': 'Anulación pendiente',
      'PROCESANDO': 'Anulación procesando',
      'PENDIENTE_SUNAT': 'Anulación en SUNAT',
      'ERROR_CONSULTA': 'Reintentando consulta',
      'ACEPTADA': 'Anulación aceptada',
      'RECHAZADA': 'Anulación rechazada',
      'ERROR_ENVIO': 'Error envío anulación'
    }[estadoAnulacion] || estadoAnulacion;

    return { color, label };
  };

  const renderEstadoFactura = (nv) => {
    if (nv.estado === 'ANULADA' || nv.estado_registro === 0) {
      return (
        <Tooltip title="Anulada">
          <Chip icon={<Cancel color="error" />} label="Anulada" color="error" />
        </Tooltip>
      );
    }

    if (nv.estado_cpe) {
      const cpeMeta = getCpeStatusMeta(nv.estado_cpe);
      const anulacionMeta = nv.ultimo_estado_anulacion ? getAnulacionStatusMeta(nv.ultimo_estado_anulacion) : null;

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title={`CPE: ${nv.estado_cpe}`}>
            <Chip
              label={cpeMeta.label}
              color={cpeMeta.color}
              variant="outlined"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Tooltip>
          {anulacionMeta && (
            <Tooltip title={nv.ultimo_mensaje_anulacion || `Anulación: ${nv.ultimo_estado_anulacion}`}>
              <Chip
                label={anulacionMeta.label}
                color={anulacionMeta.color}
                variant="filled"
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            </Tooltip>
          )}
        </Box>
      );
    }

    const estado = estadoVencimiento(nv.fecha_validez);
    if (estado === 'VENCIDA') {
      return (
        <Tooltip title="Vencida">
          <Chip icon={<Cancel color="error" />} label="Vencida" color="error" />
        </Tooltip>
      );
    }
    if (estado === 'POR VENCER') {
      return (
        <Tooltip title="Por vencer">
          <Chip icon={<Warning color="warning" />} label="Por vencer" color="warning" />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Vigente">
        <Chip icon={<CheckCircle color="success" />} label="Vigente" color="success" />
      </Tooltip>
    );
  };

  const actualizarResolucionFormato = async (nuevoFormato) => {
    setPdfResolucionCargando(true);
    try {
      const res = await axios.get(`/api/reporte-pdf/resuelve/${facturaPdfTarget.id}`, { 
        params: { formato: nuevoFormato } 
      });
      setPdfResolucion(res.data || {});
    } catch (err) {
      console.error('Error updating PDF template resolution:', err);
    } finally {
      setPdfResolucionCargando(false);
    }
  };

  const abrirDialogoPdf = async (factura) => {
    const formatoGuardado = localStorage.getItem('factura_pdf_formato_default') || 'A4';
    setFacturaPdfTarget(factura);
    setPdfFormatoSeleccionado(formatoGuardado);
    setGuardarPdfComoPredeterminado(true);
    setOpenPdfDialog(true);
    
    // Cargar resolución de plantilla
    setPdfResolucionCargando(true);
    try {
      const res = await axios.get(`/api/reporte-pdf/resuelve/${factura.id}`, { 
        params: { formato: formatoGuardado } 
      });
      setPdfResolucion(res.data || {});
    } catch (err) {
      console.error('Error resolving PDF template:', err);
      setPdfResolucion({});
    } finally {
      setPdfResolucionCargando(false);
    }
  };

  const cerrarDialogoPdf = () => {
    if (pdfDescargando) return;
    setOpenPdfDialog(false);
    setFacturaPdfTarget(null);
  };

  const descargarPdfFactura = () => {
    if (!facturaPdfTarget) return;

    const formato = String(pdfFormatoSeleccionado || 'A4').toUpperCase();
    if (guardarPdfComoPredeterminado) {
      localStorage.setItem('factura_pdf_formato_default', formato);
    }
    const tk = localStorage.getItem('token') || '';
    const query = new URLSearchParams({ formato, mode: 'inline', _ts: Date.now() }).toString();
    window.open(`/api/reporte-pdf/comprobante/${facturaPdfTarget.id}?${query}&token=${tk}`, '_blank');
    cerrarDialogoPdf();
  };

  // Búsqueda asíncrona de clientes en el Autocomplete
  useEffect(() => {
    if (open) {
      fetchClientesAsync(''); // cargar inicial
    }
  }, [open]);

  useEffect(() => {
    if (skipClienteSearch.current) { skipClienteSearch.current = false; return; }
    if (clienteInput !== '') {
      debouncedFetchClientes(clienteInput);
    } else if (open) {
      fetchClientesAsync('');
    }
  }, [clienteInput, open]);

  // Estados para Autocomplete vendedor
  const [vendedorInput, setVendedorInput] = useState('');
  const [loadingVendedores, setLoadingVendedores] = useState(false);

  // Búsqueda asíncrona de vendedores
  const fetchVendedoresAsync = async () => {
    setLoadingVendedores(true);
    if (usuarioLogueado) {
      // Mapear usuario logueado a la estructura esperada por el Autocomplete
      const vendedor = {
        id: usuarioLogueado.id,
        nombres: usuarioLogueado.nombres || usuarioLogueado.nombre || '',
        apellidos: usuarioLogueado.apellidos || '',
        username: usuarioLogueado.username || '',
        // ...otros campos si se requieren
      };
      console.log('Vendedor cargado:', vendedor);
      setVendedores([vendedor]);
      setForm(f => ({ ...f, vendedor_id: vendedor.id }));
    } else {
      setVendedores([]);
    }
    setLoadingVendedores(false);
  };

  const debouncedFetchVendedores = React.useMemo(() => debounce(fetchVendedoresAsync, 300), []);

  // Búsqueda asíncrona de vendedores en el Autocomplete
  useEffect(() => {
    if (open) {
      fetchVendedoresAsync(); // cargar inicial
    }
  }, [open]);

  // Al abrir el formulario, seleccionar por defecto Soles
  useEffect(() => {
    if (open) {
      fetchMonedasAsync('');
      setForm(f => ({ ...f, moneda: 'PEN' }));
    }
  }, [open]);


  // Facturas filtradas por búsqueda
  const filteredFacturas = facturas.filter(nv => {
    const cliente = clientes.find(x => x.id === nv.cliente_id);
    const clienteStr = cliente ? `${cliente.razon_social || ''} ${cliente.numero_documento || ''}` : '';
    return (
      (nv.serie && nv.serie.toLowerCase().includes(search.toLowerCase())) ||
      (nv.numero && nv.numero.toString().includes(search)) ||
      clienteStr.toLowerCase().includes(search.toLowerCase()) ||
      (nv.total && nv.total.toString().includes(search)) ||
      (nv.fecha_emision && nv.fecha_emision.toString().includes(search))
    );
  })
  .sort((a, b) => {
    // Ordenar por fecha de emisión descendente y luego por id descendente
    const fechaA = new Date(a.fecha_emision).getTime() || 0;
    const fechaB = new Date(b.fecha_emision).getTime() || 0;
    if (fechaB !== fechaA) return fechaB - fechaA;
    return Number(b.id || 0) - Number(a.id || 0);
  });

  // Exportar a Excel
  const handleExportExcel = async () => {
    try {
      await exportFacturasWorkbook({
        facturas: filteredFacturas,
        clientes,
        searchText: search,
        usuario: usuarioLogueado?.username || usuarioLogueado?.usuario || usuarioLogueado?.email || ''
      });
    } catch (error) {
      alert(`No se pudo generar el Excel profesional: ${error.message}`);
    }
  };

  // Paginado
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };
  const paginatedFacturas = rowsPerPage === -1 ? filteredFacturas : filteredFacturas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Lógica para jalar referencias seleccionadas y agregarlas a la Factura
  const jalarCotizacionesSeleccionadas = () => {
    const refs = cotizacionesDisponibles.filter(ref => cotizacionesSeleccionadas.includes(ref.id));
    const idField = tipoReferencia === 'cotizacion' ? 'cotizacion_id'
                  : tipoReferencia === 'nota_venta' ? 'nota_venta_id'
                  : 'guia_remision_id';
    const detalleIdField = tipoReferencia === 'cotizacion' ? 'cotizacion_detalle_id'
                         : tipoReferencia === 'nota_venta' ? 'nota_venta_detalle_id'
                         : 'guia_remision_detalle_id';
    const idsJalados = form.detalles.map(d => d[idField]);
    const nuevosDetalles = refs.flatMap(ref =>
      idsJalados.includes(ref.id) ? [] :
      (Array.isArray(ref.detalles) ? ref.detalles : []).map(det => {
        const item = {
          ...det,
          origen_tipo: tipoReferencia,
          [idField]: ref.id,
          [detalleIdField]: det.id,
          descripcion_producto: det.descripcion_producto || det.descripcion || '',
          unidad_medida: det.unidad_medida || det.unidad || '',
        };
        if (tipoReferencia === 'guia_remision' && det.producto_id) {
          const prod = productos.find(p => p.id === Number(det.producto_id));
          if (prod) {
            item.codigo_producto = prod.codigo || '';
            if (!item.precio_unitario) item.precio_unitario = prod.precio || 0;
          }
          // La guía no guarda almacén por línea, solo en la cabecera (punto_partida_almacen_id).
          if (!item.almacen_id) {
            item.almacen_id = ref.punto_partida_almacen_id || (almacenes.length === 1 ? almacenes[0].id : '');
          }
          // La guía no tiene precio/IGV por línea (no es documento de venta) — a
          // diferencia de cotización/NV, hay que calcular subtotal/igv/total aquí,
          // igual que cuando se agrega un producto manualmente.
          return calcularDetalle(item);
        }
        return item;
      })
    );
    setForm(f => ({
      ...f,
      detalles: [...f.detalles, ...nuevosDetalles]
    }));
    // Auto-registrar guías jaladas en guiasReferencia para que aparezcan en el PDF
    if (tipoReferencia === 'guia_remision') {
      const guiasNuevas = refs
        .map(ref => `${ref.serie}-${String(ref.numero).padStart(8, '0')}`)
        .filter(g => g && !guiasReferencia.includes(g));
      if (guiasNuevas.length > 0) {
        setGuiasReferencia(prev => [...prev, ...guiasNuevas.filter(g => !prev.includes(g))]);
      }
    }
    setOpenCotizaciones(false);
    setCotizacionesSeleccionadas([]);
  };

  // Al abrir el modal, limpiar selección y filtrar cotizaciones disponibles
  useEffect(() => {
    if (openCotizaciones) {
        setCotizacionesSeleccionadas([]);
        // Filtrar cotizaciones que ya están en el detalle
        const cotizacionesJaladas = form.detalles.map(d => d.cotizacion_id);
        // Si hay cliente, filtra por su número de documento
        if (form.cliente_id) {
          const cliente = clientes.find(c => c.id === form.cliente_id);
          if (cliente && cliente.numero_documento) {
            setFiltroCotizacion(cliente.numero_documento);
          } else {
            setFiltroCotizacion('');
          }
        } else {
          setFiltroCotizacion('');
        }
        setCotizacionesPage(0);
        fetchCotizaciones(); // Siempre llama para mostrar el listado
    }
    }, [openCotizaciones]);

  // Al cancelar, limpiar selección
  const handleCancelarCotizaciones = () => {
    setOpenCotizaciones(false);
    setCotizacionesSeleccionadas([]);
  };

  // Almacenes disponibles según la sucursal seleccionada (igual que en Guía de Remisión):
  // se incluyen los de la sucursal elegida y los que no tienen sucursal asignada (compartidos)
  const almacenesDisponibles = almacenes.filter(a => {
    const activo = Number(a.estado ?? 1) === 1;
    if (!activo) return false;
    if (!form.sucursal_id) return true;
    if (a.sucursal_id == null) return true;
    return Number(a.sucursal_id) === Number(form.sucursal_id);
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1800, mt: { xs: '0em', sm: '0em', md: '0em' } }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3, fontWeight: 'bold', color: '#222' }}>Facturas</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
          <TextField
            placeholder="Buscar por serie, número o cliente..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
          />
          <Button
            variant="outlined"
            color="primary"
            startIcon={isRefreshingList ? <CircularProgress size={16} /> : <Refresh />}
            onClick={fetchFacturas}
            disabled={isRefreshingList}
            sx={{ minWidth: 170, borderRadius: 2 }}
          >
            {isRefreshingList ? 'Actualizando...' : 'Actualizar listado'}
          </Button>
          <Tooltip title="Refresca estados SUNAT sin perder filtros">
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', px: 1 }}>
              <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                Ult. actualización: {formatLastRefresh(lastRefreshAt)}
              </Typography>
            </Box>
          </Tooltip>
          <Button variant="contained" color="success" startIcon={<GridOn />} onClick={handleExportExcel} sx={{ minWidth: 150, display: { xs: 'none', sm: 'flex' } }}>
            Exportar Excel
          </Button>
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleOpen} sx={{ minWidth: 120 }}>
            {isMobile ? 'Nueva' : 'Nueva Factura'}
          </Button>
          <TextField
            size="small"
            label="Mostrar"
            type="number"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            sx={{ width: 100, display: { xs: 'none', sm: 'block' } }}
            inputProps={{ min: 1 }}
          />
        </Box>
        <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mt: 2, overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Serie</TableCell>
                <TableCell align="center">Número</TableCell>
                <TableCell align="center">Cliente</TableCell>
                <TableCell align="center">Fecha Emisión</TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Vence</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Total</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedFacturas.map(nv => (
                <TableRow key={nv.id}>
                  <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{nv.serie}</TableCell>
                  <TableCell align="center">
                    {nv.numero}
                    {nv.origen_referencia && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem', lineHeight: 1.2 }}>
                        ↳ {nv.origen_referencia}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{
                    (() => {
                      const cliente = clientes.find(x => x.id === nv.cliente_id);
                      if (cliente) {
                        const nombreCliente = cliente.razon_social || `${cliente.nombres || ''} ${cliente.apellidos || ''}`.trim();
                        return `${nombreCliente}${cliente.numero_documento ? ` (${cliente.numero_documento})` : ''}`;
                      } else if (nv.cliente_id) {
                        return `ID: ${nv.cliente_id}`;
                      } else {
                        return 'Sin cliente';
                      }
                    })()
                  }</TableCell>
                  <TableCell align="center">{nv.fecha_emision?.substring(0, 10)}</TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{nv.fecha_validez?.substring(0, 10)}</TableCell>
                  <TableCell align="center">{renderEstadoFactura(nv)}</TableCell>
                  <TableCell align="center">{nv.total}</TableCell>
                  <TableCell align="center">
                    {/* Botones de acciones con colores y nuevas funciones */}
                    <Tooltip title={
                      nv.estado_cpe === 'ENVIADO' ? "No se puede editar: Comprobante enviado a SUNAT" :
                      nv.estado_cpe === 'RECHAZADO' ? "No se puede editar: Comprobante rechazado por SUNAT" :
                      nv.estado_cpe === 'ANULADO' ? "No se puede editar: Comprobante anulado" :
                      "Editar"
                    }>
                      <IconButton color="primary" onClick={async () => {
                        try {
                          const res = await axios.get(`/api/comprobante/${nv.id}`);
                          const nov = res.data;
                          setModoEdicion(true);
                          setOpen(true);
                          setForm(prev => ({
                            ...prev,
                            ...nov,
                            detalles: Array.isArray(nov.detalles) ? nov.detalles : (typeof nov.detalles === 'string' && nov.detalles && nov.detalles.trim().startsWith('[') ? JSON.parse(nov.detalles) : []),
                            fecha_emision: nov.fecha_emision ? nov.fecha_emision.substring(0, 10) : '',
                            fecha_validez: nov.fecha_validez ? nov.fecha_validez.substring(0, 10) : '',
                            adjuntos: Array.isArray(nov.adjuntos) ? nov.adjuntos : (typeof nov.adjuntos === 'string' && nov.adjuntos && nov.adjuntos.trim().startsWith('[') ? JSON.parse(nov.adjuntos) : []),
                            serie: nov.serie,
                            numero: nv.numero, // Forzar el correlativo de la lista seleccionada
                            cliente_id: nov.cliente_id,
                            vendedor_id: nov.vendedor_id,
                            moneda: nov.moneda,
                            tipo_cambio: nov.tipo_cambio,
                            condicion_pago: nov.condicion_pago_id || nov.condicion_pago,
                            cuotas_json: nov.cuotas_json,
                            subtotal: nov.subtotal,
                            descuento_global: nov.descuento_global,
                            porcentaje_descuento_global: nov.porcentaje_descuento_global,
                            total_igv: nov.total_igv,
                            total: nov.total,
                            observaciones: nov.observaciones,
                            estado: nov.estado,
                            sucursal_id: nov.sucursal_id,
                            detraccion_porcentaje_personalizado: false,
                            detraccion_porcentaje: 12
                          }));
                          // Si la detracción usó un % personalizado, recuperarlo (guardado aparte,
                          // no viaja en el objeto del comprobante)
                          if (nov.detraccion_aplicada) {
                            try {
                              const resPct = await axios.get(`/api/datos-adicionales/FACTURA/${nov.id}/detraccion_porcentaje`);
                              const pctGuardado = resPct?.data?.valor;
                              if (pctGuardado) {
                                setForm(f => ({ ...f, detraccion_porcentaje_personalizado: true, detraccion_porcentaje: Number(pctGuardado) }));
                              }
                            } catch { /* sin % guardado, se deja el modo manual actual */ }
                          }
                          // Manejar el check de preciosIncluyenIgv
                          if (nov.hasOwnProperty('preciosIncluyenIgv')) {
                            setPreciosIncluyenIgv(!!nov.preciosIncluyenIgv);
                          } else if (nov.hasOwnProperty('precios_incluyen_igv')) {
                            setPreciosIncluyenIgv(nov.precios_incluyen_igv === 1 || nov.precios_incluyen_igv === true);
                          } else {
                            // Si no viene en la cotización, usar config empresa
                            try {
                              const { obtenerEmpresaConfig } = await import('../utils/empresaConfig');
                              const config = await obtenerEmpresaConfig();
                              if (config && (config.precios_incluyen_igv === 1 || config.precios_incluyen_igv === true)) {
                                setPreciosIncluyenIgv(true);
                              } else {
                                setPreciosIncluyenIgv(false);
                              }
                            } catch (err) {
                              setPreciosIncluyenIgv(false);
                            }
                          }
                        } catch (err) {
                          alert('Error al obtener detalles de la factura.');
                        }
                      }}
                      disabled={nv.estado === 'ANULADA' || nv.estado_registro === 0 || nv.estado_cpe === 'ENVIADO' || nv.estado_cpe === 'RECHAZADO' || nv.estado_cpe === 'ANULADO'}
                      >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Anular Electrónicamente">
                    <IconButton color="error" onClick={() => {
                      setFacturaAAnular(nv);
                      setMotivoAnulacion('');
                      setProcesandoAnulacion(false);
                      setOpenAnulacion(true);
                    }}
                    disabled={nv.estado === 'ANULADA' || nv.estado_registro === 0 || !['ACEPTADO', 'ENVIADO'].includes(nv.estado_cpe) || ['PENDIENTE', 'PROCESANDO', 'PENDIENTE_SUNAT', 'PENDIENTE_RESUMEN', 'PENDIENTE_SUNAT_RESUMEN', 'PROCESANDO_RESUMEN', 'ERROR_CONSULTA'].includes(nv.ultimo_estado_anulacion)}
                    >
                    <Gavel />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Ver">
                    <IconButton color="success" onClick={async () => {
                      try {
                        const res = await axios.get(`/api/comprobante/${nv.id}`);
                        const nota = res.data;
                        // Parsear detalles y adjuntos si vienen como string
                        let detalles = Array.isArray(nota.detalles) ? nota.detalles : (typeof nota.detalles === 'string' && nota.detalles ? JSON.parse(nota.detalles) : []);
                        let adjuntos_json = Array.isArray(nota.adjuntos_json) ? nota.adjuntos_json : (typeof nota.adjuntos_json === 'string' && nota.adjuntos_json ? JSON.parse(nota.adjuntos_json) : []);
                        let estadoVisual = estadoVencimiento(nota.fecha_validez);
                        if (!nota.fecha_validez) {
                          estadoVisual = 'VIGENTE';
                        }
                        setVerFactura({ ...nota, detalles, adjuntos_json, estadoVisual });
                      } catch (err) {
                        alert('Error al obtener detalles de la factura.');
                      }
                    }}>
                      <CheckCircle />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="PDF">
                    <IconButton color="secondary" onClick={async () => {
                      abrirDialogoPdf(nv);
                    }}>
                      <CloudUpload />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Descargar XML">
                    <span>
                      <IconButton
                        color="info"
                        disabled={!(findAdjuntoByTipo(nv.adjuntos_json, 'xml') || nv.xml_firmado_path)}
                        onClick={(e) => {
                          e.stopPropagation();
                          const nombre = findAdjuntoByTipo(nv.adjuntos_json, 'xml');
                          const tk = localStorage.getItem('token') || '';
                          if (nombre) {
                            window.open(`/api/comprobante/adjunto/${nv.serie}/${nv.numero}/${nombre}?token=${tk}`, '_blank');
                            return;
                          }
                          window.open(`/api/comprobante/${nv.id}/archivo-cpe/xml?token=${tk}`, '_blank');
                        }}
                      >
                        <Description />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Descargar CDR">
                    <span>
                      <IconButton
                        color="success"
                        disabled={!(findAdjuntoByTipo(nv.adjuntos_json, 'cdr') || nv.cdr_path)}
                        onClick={(e) => {
                          e.stopPropagation();
                          const nombre = findAdjuntoByTipo(nv.adjuntos_json, 'cdr');
                          const tk = localStorage.getItem('token') || '';
                          if (nombre) {
                            window.open(`/api/comprobante/adjunto/${nv.serie}/${nv.numero}/${nombre}?token=${tk}`, '_blank');
                            return;
                          }
                          window.open(`/api/comprobante/${nv.id}/archivo-cpe/cdr?token=${tk}`, '_blank');
                        }}
                      >
                        <Archive />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      puedeReenviarCpe(nv)
                        ? 'Reenviar CPE a SUNAT'
                        : 'No disponible: si fue rechazado o anulado, emita un comprobante nuevo'
                    }
                  >
                    <span>
                      <IconButton
                        color="warning"
                        disabled={!puedeReenviarCpe(nv) || reenviandoCpeId === nv.id}
                        onClick={() => reenviarCpe(nv)}
                      >
                        {reenviandoCpeId === nv.id ? <CircularProgress size={18} /> : <Refresh />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Enviar por WhatsApp">
                    <IconButton size="small" sx={{ color: '#25d366' }}
                      onClick={() => setWspDialog({ open: true, data: nv })}>
                      <WhatsAppIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Enviar por correo electrónico">
                    <IconButton size="small" color="primary"
                      onClick={() => setEmailDialog({ open: true, data: nv })}>
                      <EmailIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                        {/* Modal para ver factura */}
                        {verFactura && (
                          <Dialog open={true} onClose={() => setVerFactura(null)} maxWidth="md" fullWidth>
                            <DialogTitle sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Receipt /> Detalle de Factura
                            </DialogTitle>
                            <DialogContent sx={{ bgcolor: '#fafbfc', pt: '20px !important' }}>
                              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                <Typography variant="h6" fontWeight="bold" color="#1976d2" gutterBottom>
                                  {clientes.find(x => x.id === verFactura.cliente_id)?.razon_social || ''}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                                  <Typography variant="body2"><strong>Serie-Número:</strong> {verFactura.serie}-{verFactura.numero}</Typography>
                                  <Typography variant="body2"><strong>Fecha Emisión:</strong> {verFactura.fecha_emision?.substring(0, 10) || ''}</Typography>
                                  <Typography variant="body2"><strong>Moneda:</strong> {verFactura.moneda}</Typography>
                                  <Typography variant="body2"><strong>Condición Pago:</strong> {verFactura.condicion_pago || ''}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" fontWeight="bold">Estado:</Typography>
                                  <Chip size="small" variant="outlined" color="info" label={
                                    verFactura.estado === 'PENDIENTE'
                                      ? (verFactura.estadoVisual || verFactura.estado)
                                      : verFactura.estado
                                  } />
                                </Box>
                                {verFactura.observaciones && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    <strong>Observaciones:</strong> {verFactura.observaciones}
                                  </Typography>
                                )}
                              </Paper>
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ bgcolor: '#1976d2' }}>
                                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Código</TableCell>
                                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Producto</TableCell>
                                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Almacén</TableCell>
                                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Cantidad</TableCell>
                                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>U.Medida</TableCell>
                                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Precio</TableCell>
                                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Subtotal</TableCell>
                                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>IGV</TableCell>
                                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Total</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(Array.isArray(verFactura.detalles) ? verFactura.detalles : (typeof verFactura.detalles === 'string' && verFactura.detalles && verFactura.detalles.trim().startsWith('[') ? JSON.parse(verFactura.detalles) : [])).map((d, idx) => (
                                      <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? '#f5f5f5' : '#fff' }}>
                                        <TableCell>{d.codigo_producto || productos.find(p => p.id === d.producto_id)?.codigo || ''}</TableCell>
                                        <TableCell>{d.descripcion_producto || productos.find(p => p.id === d.producto_id)?.nombre || ''}</TableCell>
                                        <TableCell>{almacenes.find(a => Number(a.id) === Number(d.almacen_id))?.nombre || ''}</TableCell>
                                        <TableCell>{d.cantidad}</TableCell>
                                        <TableCell>{d.unidad_medida}</TableCell>
                                        <TableCell>{d.precio_unitario}</TableCell>
                                        <TableCell>{d.subtotal}</TableCell>
                                        <TableCell>{d.igv_monto}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{d.total}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              {/* Adjuntos */}
                              {verFactura.adjuntos_json && typeof verFactura.adjuntos_json === 'string' && verFactura.adjuntos_json.trim().startsWith('[') && JSON.parse(verFactura.adjuntos_json).length > 0 && (
                                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                                  <Typography variant="subtitle1" fontWeight="bold" color="#1976d2" gutterBottom>Documentos Adjuntos</Typography>
                                  {JSON.parse(verFactura.adjuntos_json).map((nombre, idx) => (
                                    <Button key={idx} variant="outlined" color="info" startIcon={getAdjuntoStartIcon(nombre)} onClick={() => {
                                      // Descarga directa desde la ruta correcta
                                      const link = document.createElement('a');
                                      link.href = `/api/comprobante/adjunto/${verFactura.serie}/${verFactura.numero}/${nombre}?token=${localStorage.getItem('token') || ''}`;
                                      link.target = '_blank';
                                      link.download = nombre;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }} sx={{ mr: 1, mb: 1 }}>
                                      {nombre}
                                    </Button>
                                  ))}
                                </Paper>
                              )}
                            </DialogContent>
                            <DialogActions sx={{ p: 2 }}>
                              <Button onClick={() => setVerFactura(null)} startIcon={<Cancel />} variant="outlined">Cerrar</Button>
                            </DialogActions>
                          </Dialog>
                        )}
                  {/* Botón para descargar/ver adjuntos */}
                  {nv.adjuntos_json && JSON.parse(nv.adjuntos_json).length > 0 && (
                    <Tooltip title="Descargar adjuntos">
                      <IconButton color="info" onClick={e => {
                        e.stopPropagation();
                        const tk = localStorage.getItem('token') || '';
                        JSON.parse(nv.adjuntos_json).forEach((nombre, i) => {
                          setTimeout(() => {
                            const a = document.createElement('a');
                            a.href = `/api/comprobante/adjunto/${nv.serie}/${nv.numero}/${nombre}?token=${tk}`;
                            a.download = nombre;
                            document.body.appendChild(a); a.click(); document.body.removeChild(a);
                          }, i * 500);
                        });
                      }}>
                        <AttachFile />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginacionControl page={page} rowsPerPage={rowsPerPage} count={filteredFacturas.length} onPageChange={handleChangePage} />
      <Dialog open={open} onClose={(_, r) => r !== 'backdropClick' && handleClose()} disableEscapeKeyDown={false} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 'bold', fontSize: { xs: 17, sm: 22 }, letterSpacing: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Receipt sx={{ mr: 1, mb: -0.5 }} /> {modoEdicion ? 'Editar Factura' : 'Nueva Factura'}
          </Box>
          {isMobile && (<IconButton size="small" onClick={handleClose} sx={{ color: '#fff' }}><Close /></IconButton>)}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 2 }} justifyContent={'center'}>
            <Grid item xs={4} sx={{ width: { xs: '100%', sm: '30em' }, minWidth: { xs: 0, sm: '30em' } }}>
              <Autocomplete
                options={sucursales}
                getOptionLabel={option => option.nombre ? `${option.nombre}` : ''}
                value={sucursales.find(s => s.id === form.sucursal_id) || null}
                inputValue={sucursalInput}
                onInputChange={(e, value) => setSucursalInput(value)}
                onChange={(e, value) => { if (value) setForm(f => ({ ...f, sucursal_id: value.id })); }}
                loading={loadingSucursales}
                disableClearable
                renderInput={params => (
                  <TextField {...params} label="Sucursal" fullWidth required InputProps={{ ...params.InputProps, endAdornment: (<>{params.InputProps.endAdornment}</>) }} />
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField type="date" label="Emisión" name="fecha_emision" value={form.fecha_emision} onChange={handleFechaEmisionChange} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={3}>
              <TextField type="date" label="Vence" name="fecha_validez" value={form.fecha_validez} onChange={handleFormChange} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={2} sx={{ width: '8em', minWidth: '8em', flexBasis: '8em !important', maxWidth: 'none !important' }}>
              <Autocomplete
                options={series}
                getOptionLabel={option => option.serie ? `${option.serie}` : ''}
                value={series.find(s => s.serie === form.serie) || null}
                inputValue={serieInput}
                onInputChange={(e, value) => setSerieInput(value)}
                onChange={(e, value) => {
                  setForm(f => ({ ...f, serie: value ? value.serie : '' }));
                  if (!modoEdicion) {
                    setForm(f => ({ ...f, numero: value ? value.correlativo_actual : '' }));
                  }
                }}
                loading={loadingSeries}
                disableClearable
                renderInput={params => (
                  <TextField {...params} label="Serie" fullWidth required InputProps={{ ...params.InputProps, endAdornment: (<>{params.InputProps.endAdornment}</>) }} />
                )}
              />
            </Grid>
            <Grid item xs={2} sx={{ width: '8em', minWidth: '8em', flexBasis: '8em !important', maxWidth: 'none !important' }}>
              <TextField label="Número" name="numero" value={form.numero} fullWidth required disabled />
            </Grid>
            <Grid item xs={4} sx={{ width: { xs: '100%', sm: '30em' }, minWidth: { xs: 0, sm: '30em' } }}>
              <Autocomplete
                options={clientes}
                getOptionLabel={option => {
                  const nombreCliente = option.razon_social || `${option.nombres || ''} ${option.apellidos || ''}`.trim();
                  return nombreCliente && option.numero_documento ? `${nombreCliente} (${option.numero_documento})` : nombreCliente;
                }}
                value={clientes.find(c => c.id === form.cliente_id) || null}
                inputValue={clienteInput}
                onInputChange={(e, value) => setClienteInput(value)}
                noOptionsText={clienteInput.length >= 2 ? (<Button size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpenNuevoCliente(true)}>+ Registrar "{clienteInput}" como nuevo cliente</Button>) : 'Escribe para buscar'}
                onChange={(e, value) => setForm({ ...form, cliente_id: value ? value.id : '' })}
                loading={loadingClientes}
                renderInput={params => (
                  <TextField {...params} label="Cliente" fullWidth required InputProps={{ ...params.InputProps, endAdornment: (<>{params.InputProps.endAdornment}</>) }} />
                )}
              />
            </Grid>
            <Grid item xs={2}  sx={{ width: '13em', minWidth: '13em' }}>
              <Autocomplete
                options={monedas}
                getOptionLabel={option => option.descripcion && option.codigo ? `${option.descripcion} (${option.codigo})` : option.descripcion || ''}
                value={monedas.find(m => m.codigo === form.moneda) || null}
                inputValue={monedaInput}
                onInputChange={(e, value) => setMonedaInput(value)}
                onChange={async (e, value) => {
                  const nuevaMoneda = value ? value.codigo : '';
                  setForm(f => ({ ...f, moneda: nuevaMoneda }));
                  if (nuevaMoneda === 'USD') {
                    const fecha = form.fecha_emision;
                    try {
                      // 1. Buscar en la BD
                      const resBD = await axios.get('/api/tipo-cambio');
                      const tc = resBD.data.find(x => x.fecha && x.fecha.substring(0,10) === fecha);
                      if (tc && tc.venta) {
                        setForm(f => ({ ...f, tipo_cambio: tc.venta }));
                        return;
                      }
                    } catch {}
                    try {
                      // 2. Si no existe en BD, buscar en API
                      const res = await axios.get(`/api/tipo-cambio/sunat?fecha=${fecha}`);
                      if (res.data && res.data.venta) {
                        setForm(f => ({ ...f, tipo_cambio: res.data.venta }));
                        return;
                      }
                    } catch {}
                    // 3. Si falla todo, poner 1
                    setForm(f => ({ ...f, tipo_cambio: 1 }));
                  } else {
                    setForm(f => ({ ...f, tipo_cambio: 1 }));
                  }
                }}
                loading={loadingMonedas}
                disableClearable
                renderInput={params => (
                  <TextField {...params} label="Moneda" fullWidth required InputProps={{ ...params.InputProps, endAdornment: (<>{params.InputProps.endAdornment}</>) }} />
                )}
              />
            </Grid>
            <Grid item xs={2} sx={{ width: '8em', minWidth: '8em' }}>
              <TextField label="Tipo Cambio" name="tipo_cambio" value={form.tipo_cambio} onChange={handleFormChange} fullWidth />
            </Grid>
            <Grid item xs={2} sx={{ width: { xs: '100%', sm: '15.6em' }, minWidth: { xs: 0, sm: '15.6em' } }}>
              <Autocomplete
                options={vendedores}
                getOptionLabel={option => option.nombres && option.apellidos && option.username ? `${option.nombres} ${option.apellidos} (${option.username})` : option.nombres || ''}
                value={vendedores.find(v => v.id === form.vendedor_id) || null}
                inputValue={vendedores.find(v => v.id === form.vendedor_id) ? `${vendedores.find(v => v.id === form.vendedor_id).nombres} ${vendedores.find(v => v.id === form.vendedor_id).apellidos} (${vendedores.find(v => v.id === form.vendedor_id).username})` : ''}
                disabled
                renderInput={params => (
                  <TextField {...params} label="Vendedor" fullWidth required disabled />
                )}
              />
            </Grid>
            <Grid item xs={4} sx={{ width: { xs: '100%', sm: '18em' }, minWidth: { xs: 0, sm: '18em' } }}>
              <Autocomplete
                options={condicionesPago}
                getOptionLabel={option => option?.descripcion || ''}
                value={condicionesPago.find(c => c.id === form.condicion_pago) || null}
                onChange={(e, value) => setForm(f => ({ ...f, condicion_pago: value ? value.id : '' }))}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={loadingCondicionesPago}
                disableClearable
                renderInput={params => (
                  <TextField {...params} label="Condición Pago" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={8}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sx={{ minWidth: { xs: 0, sm: '27em' } }}>
                  <TextField label="Observaciones" name="observaciones" value={form.observaciones} onChange={handleFormChange} fullWidth sx={{ minWidth: { xs: 0, sm: '27em' } }} />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AddCircleIcon />}
                    fullWidth
                    sx={{ backgroundColor: '#f3e5f5', color: '#7b1fa2', fontWeight: 'bold' }}
                    onClick={handleOpenDatosAdicionales} // función para abrir el diálogo de datos adicionales
                  >
                    Datos adicionales
                  </Button>
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    color="info"
                    component="label"
                    startIcon={<AttachFile />}
                    fullWidth
                    sx={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 'bold' }}
                  >
                    Adjuntar
                    <input type="file" hidden multiple onChange={handleAdjunto} />
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6" sx={{ color: '#000000', fontWeight: 'bold' }}>Detalle de productos</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5, gap: 1, alignItems: 'center' }}>
                    <TextField
                        select
                        size="small"
                        label="Tipo"
                        value={tipoReferencia}
                        onChange={e => setTipoReferencia(e.target.value)}
                        sx={{ minWidth: 170 }}
                    >
                        <MenuItem value="cotizacion">Cotización</MenuItem>
                        <MenuItem value="nota_venta">Nota de Venta</MenuItem>
                        <MenuItem value="guia_remision">Guía de Remisión</MenuItem>
                    </TextField>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setOpenCotizaciones(true)}
                        sx={{ minWidth: 180, fontWeight: 'bold' }}
                    >
                        + Adicionar referencia
                    </Button>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={copiarNombreADesc}
                        onChange={e => setCopiarNombreADesc(e.target.checked)}
                        color="secondary"
                        size="small"
                      />
                    }
                    label={<Typography variant="caption">Copiar nombre → descripción</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={preciosIncluyenIgv}
                        onChange={e => setPreciosIncluyenIgv(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={<Typography variant="caption">Precios incluyen IGV</Typography>}
                  />
                </Box>
              </Box>
              <TableContainer component={Paper} sx={{ mb: 2, overflowX: 'auto' }}>
                <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 680 }}>
                  <colgroup>
                    <col style={{ width: colW.codigo }} />
                    <col style={{ width: colW.producto }} />
                    <col style={{ width: colW.cant }} />
                    <col style={{ width: colW.umedida }} />
                    <col style={{ width: colW.precio }} />
                    <col style={{ width: colW.dscto }} />
                    <col style={{ width: colW.total }} />
                    <col style={{ width: colW.almacen }} />
                    <col style={{ width: colW.acciones }} />
                  </colgroup>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#2e8ae6' }}>
                      {[
                        { key: 'codigo',   label: 'Código' },
                        { key: 'producto', label: 'Producto' },
                        { key: 'cant',     label: 'Cant.' },
                        { key: 'umedida',  label: 'U.Med' },
                        { key: 'precio',   label: 'Precio' },
                        { key: 'dscto',    label: 'Dscto' },
                        { key: 'total',    label: 'Total' },
                        { key: 'almacen',  label: 'Almacén' },
                        { key: 'acciones', label: 'Acciones' },
                      ].map(col => (
                        <TableCell key={col.key} align="center" sx={{
                          position: 'relative', color: '#fff', fontWeight: 'bold',
                          overflow: 'hidden', whiteSpace: 'nowrap', userSelect: 'none',
                          px: 1,
                        }}>
                          {col.label}
                          {col.key !== 'acciones' && (
                            <ResizeHandleFactura onMouseDown={e => startResize(col.key, e)} />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {form.detalles.map((d, idx) => (
                      <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? '#f5f5f5' : '#fff' }}>
                        <TableCell align="center" sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', px: 1 }}>
                          {d.codigo_producto || ''}
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', px: 1 }}>
                          <Tooltip title={d.descripcion_producto || ''}>
                            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {d.descripcion_producto || productos.find(p => p.id === d.producto_id)?.nombre || ''}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={{ px: 1 }}>{d.cantidad}</TableCell>
                        <TableCell align="center" sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', px: 1 }}>
                          {unidades.find(u => u.id === d.unidad_medida)?.abreviatura || d.unidad_medida}
                        </TableCell>
                        <TableCell align="right" sx={{ px: 1 }}>{d.precio_unitario}</TableCell>
                        <TableCell align="center" sx={{ px: 1 }}>{d.descuento_monto || d.descuento_porcentaje || ''}</TableCell>
                        <TableCell align="right" sx={{ px: 1, fontWeight: 'bold' }}>{d.total}</TableCell>
                        <TableCell sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', px: 1 }}>
                          {almacenes.find(a => Number(a.id) === Number(d.almacen_id))?.nombre || ''}
                        </TableCell>
                        <TableCell align="center" sx={{ px: 0.5 }}>
                          <Tooltip title="Editar"><IconButton size="small" color="primary" onClick={() => editarDetalle(idx)}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => eliminarDetalle(idx)}><Delete fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container spacing={1} alignItems="center" justifyContent={'center'}>
                <Grid item xs={4} sx={{ width: { xs: '100%', sm: '20em' }, minWidth: { xs: 0, sm: '20em' } }}>
                  <Autocomplete
                    options={productos}
                    getOptionLabel={option => option?.nombre || ''}
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option.codigo && (
                          <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.78em', mr: 1, minWidth: '4.5em', flexShrink: 0 }}>
                            {option.codigo}
                          </Box>
                        )}
                        {option.nombre}
                      </li>
                    )}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      const v = inputValue.toLowerCase();
                      const filtered = options.filter(o =>
                        (o.nombre || '').toLowerCase().includes(v) ||
                        (o.codigo || '').toLowerCase().includes(v)
                      );
                      const selId = detalle.producto_id;
                      if (selId && !filtered.some(o => o.id === selId)) {
                        const sel = options.find(o => o.id === selId);
                        if (sel) return [sel, ...filtered];
                      }
                      return filtered;
                    }}
                    value={productos.find(p => p.id === detalle.producto_id) || null}
                    inputValue={productoInput}
                    onInputChange={(e, value, reason) => {
                      if (reason === 'input') {
                        setProductoInput(value);
                        debouncedFetchProductos(value);
                      } else if (reason === 'clear') {
                        setProductoInput('');
                        debouncedFetchProductos('');
                      }
                    }}
                    noOptionsText={productoInput.length >= 2 ? (<Button size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpenNuevoProducto(true)}>+ Registrar "{productoInput}" como nuevo producto</Button>) : 'Escribe para buscar'}
                    onChange={(e, value) => {
                      handleDetalleChange({ target: { name: 'producto_id', value: value ? value.id : '' } });
                      setProductoInput(value ? (value.nombre || '') : '');
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={params => (
                      <TextField {...params} label="Producto" fullWidth error={detalleError.producto} />
                    )}
                  />
                </Grid>
                <Grid item xs={4} sx={{ width: { xs: '100%', sm: '21em' }, minWidth: { xs: 0, sm: '21em' } }}>
                  <TextField
                    label="Descripción de producto"
                    name="descripcion_producto"
                    value={detalle.descripcion_producto}
                    onChange={handleDetalleChange}
                    fullWidth
                    onKeyDown={e => { if (e.key === 'F1') { e.preventDefault(); setOpenBuscadorProducto(true); } }}
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Buscar producto (F1)">
                          <IconButton size="small" onClick={() => setOpenBuscadorProducto(true)} edge="end">
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={1} sx={{ width: '6.5em', minWidth: '6.5em' }}>
                  <Tooltip title={editingDetalle !== null && ORIGENES_YA_AFECTAN_KARDEX.includes(detalle.origen_tipo)
                    ? 'Esta línea viene de una guía o nota de venta que ya descontó stock; corrige el documento de origen si la cantidad está mal.'
                    : ''}>
                    <span>
                      <TextField label="Cant." name="cantidad" value={detalle.cantidad} onChange={handleDetalleChange} fullWidth
                        error={detalleError.cantidad}
                        disabled={editingDetalle !== null && ORIGENES_YA_AFECTAN_KARDEX.includes(detalle.origen_tipo)} />
                    </span>
                  </Tooltip>
                </Grid>
                <Grid item xs={1} sx={{ width: '6.5em', minWidth: '6.5em' }}>
                  <TextField label="Precio" name="precio_unitario" value={detalle.precio_unitario} onChange={handleDetalleChange} fullWidth />
                </Grid>
                <Grid item xs={1} sx={{ width: '6.5em', minWidth: '6.5em' }}>
                  <TextField label="Desc. S/" name="descuento_monto" value={detalle.descuento_monto} onChange={handleDetalleChange} fullWidth />
                </Grid>
                <Grid item xs={1} sx={{ width: '6.5em', minWidth: '6.5em' }}>
                  <TextField label="Desc. %" name="descuento_porcentaje" value={detalle.descuento_porcentaje} onChange={handleDetalleChange} fullWidth />
                </Grid>
                {isMobile && <div style={{ flexBasis: '100%', width: '100%', height: 0 }} />}
                <Grid item xs={4} sx={{ width: { xs: '100%', sm: '24em' }, minWidth: { xs: 0, sm: '24em' }, mt: { xs: 0, sm: 1 } }}>
                  <Autocomplete
                    options={Array.isArray(tiposIGV) ? tiposIGV : []}
                    getOptionLabel={option => option?.descripcion || ''}
                    value={Array.isArray(tiposIGV) ? tiposIGV.find(t => t.codigo === detalle.tipo_igv) || null : null}
                    onChange={(e, value) => {
                      handleDetalleChange({ target: { name: 'tipo_igv', value: value ? value.codigo : '' } });
                    }}
                    isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
                    loading={loadingTiposIGV}
                    disableClearable
                    renderInput={params => (
                      <TextField {...params} label="Tipo IGV" fullWidth />
                    )}
                  />
                </Grid>
                {isMobile && <div style={{ flexBasis: '100%', width: '100%', height: 0 }} />}
                <Grid item xs={2} sx={{ width: { xs: '100%', sm: '16em' }, minWidth: { xs: 0, sm: '16em' }, mt: { xs: 0, sm: 1 } }}>
                  <Autocomplete
                    options={unidades}
                    getOptionLabel={option => option?.nombre ? `${option.nombre} (${option.abreviatura})` : ''}
                    value={unidades.find(u => u.id === detalle.unidad_medida) || null}
                    onChange={(e, value) => {
                      handleDetalleChange({ target: { name: 'unidad_medida', value: value ? value.id : '' } });
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={params => (
                      <TextField {...params} label="U.Medida" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={4} sx={{ width: { xs: '100%', sm: '20em' }, minWidth: { xs: 0, sm: '20em' }, mt: { xs: 0, sm: 0.5 } }}>
                  <Autocomplete
                    options={almacenesDisponibles}
                    getOptionLabel={option => option.nombre || ''}
                    value={almacenesDisponibles.find(a => a.id === detalle.almacen_id) || null}
                    onChange={(e, value) => {
                      setDetalle(d => ({ ...d, almacen_id: value ? value.id : '' }));
                      setDetalleError(err => ({ ...err, almacen: false }));
                      setDetalleErrorMsg('');
                    }}
                    inputValue={almacenInput}
                    onInputChange={(e, value) => setAlmacenInput(value)}
                    loading={loadingAlmacenes}
                    renderInput={params => (
                      <TextField {...params} label="Almacén" size="small" fullWidth margin="dense" error={detalleError.almacen} required
                        onKeyDown={e => { if (e.key === 'F1') { e.preventDefault(); if (detalle.producto_id) setOpenStockAlmacen(true); } }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disableClearable
                  />
                </Grid>
                <Grid item xs={2} sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={editingDetalle === null ? <Add /> : <CheckCircle />}
                    onClick={editingDetalle === null ? agregarDetalle : actualizarDetalle}
                    sx={{ backgroundColor: '#e8f5e9', color: '#388e3c', fontWeight: 'bold' }}
                  >
                    {editingDetalle === null ? 'Agregar' : 'Actualizar'}
                  </Button>
                </Grid>
                {stockDisponible !== null && (() => {
                  const cantEnGrid = form.detalles
                    .filter(d => d.producto_id === detalle.producto_id && d.almacen_id === detalle.almacen_id)
                    .reduce((s, d) => s + Number(d.cantidad || 0), 0);
                  const excede = Number(detalle.cantidad) > 0 && (cantEnGrid + Number(detalle.cantidad)) > Number(stockDisponible);
                  return (
                    <Grid item xs={12} sx={{ textAlign: 'center', mt: 0.5 }}>
                      <Chip
                        label={`Stock disponible: ${parseFloat(stockDisponible).toFixed(2)}`}
                        color={excede ? 'error' : 'success'}
                        size="small"
                        variant="outlined"
                      />
                      {excede && (
                        <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5 }}>
                          {cantEnGrid > 0
                            ? `Stock insuficiente. Ya tiene ${cantEnGrid.toFixed(2)} de este producto en la grilla. Disponible: ${parseFloat(stockDisponible).toFixed(2)} unidades.`
                            : `Stock insuficiente. Disponible en almacén: ${parseFloat(stockDisponible).toFixed(2)} unidades.`}
                        </Typography>
                      )}
                    </Grid>
                  );
                })()}
                {detalleErrorMsg && (
                  <Grid item xs={12}>
                    <Typography color="error" sx={{ mb: 1 }}>{detalleErrorMsg}</Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            {isMobile ? (
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField label={`Subtotal`} value={Number(form.subtotal || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} InputProps={{ readOnly: true }} size="small" slotProps={{ input: { sx: { bgcolor: '#e3f2fd', fontWeight: 'bold' } }, htmlInput: { style: { textAlign: 'center' } } }} />
                  <TextField label={`IGV`} value={Number(form.total_igv || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} InputProps={{ readOnly: true }} size="small" slotProps={{ input: { sx: { bgcolor: '#fff9c4', fontWeight: 'bold' } }, htmlInput: { style: { textAlign: 'center' } } }} />
                  <TextField label={`Total`} value={Number(form.total || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} InputProps={{ readOnly: true }} size="small" slotProps={{ input: { sx: { bgcolor: '#e8f5e9', fontWeight: 'bold' } }, htmlInput: { style: { textAlign: 'center' } } }} />
                </Box>
              </Grid>
            ) : (
              <>
            <Grid item xs={4} sx={{ minWidth: '18em', mt: 2 }}>
              <TextField
                label={`Subtotal (${form.moneda || 'PEN'})`}
                value={Number(form.subtotal || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                fullWidth
                disabled
                slotProps={{
                  input: {
                    sx: {
                      textAlign: 'center',
                      backgroundColor: '#e3f2fd',
                      fontWeight: 'bold'
                    },
                    style: { textAlign: 'center' }
                  },
                  htmlInput: { style: { textAlign: 'center' } }
                }}
              />
            </Grid>
            <Grid item xs={4} sx={{ minWidth: '18em', mt: 2 }}>
              <TextField
                label={`IGV (${form.moneda || 'PEN'})`}
                value={Number(form.total_igv || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                fullWidth
                disabled
                slotProps={{
                  input: {
                    sx: {
                      textAlign: 'center',
                      backgroundColor: '#fff9c4',
                      fontWeight: 'bold'
                    },
                    style: { textAlign: 'center' }
                  },
                  htmlInput: { style: { textAlign: 'center' } }
                }}
              />
            </Grid>
            <Grid item xs={4} sx={{ minWidth: '18em', mt: 2 }}>
              <TextField
                label={`Total (${form.moneda || 'PEN'})`}
                value={Number(form.total || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                fullWidth
                disabled
                slotProps={{
                  input: {
                    sx: {
                      textAlign: 'center',
                      backgroundColor: '#e8f5e9',
                      fontWeight: 'bold'
                    },
                    style: { textAlign: 'center' }
                  },
                  htmlInput: { style: { textAlign: 'center' } }
                }}
              />
            </Grid>
              </>
            )}
            {form.moneda && form.moneda !== 'PEN' && Number(form.tipo_cambio) > 0 && (
              <Grid item xs={4} sx={{ minWidth: '18em', mt: 1 }}>
                <TextField
                  label="Total equivalente S/ (PEN)"
                  value={(Number(form.total || 0) * Number(form.tipo_cambio)).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  fullWidth
                  disabled
                  slotProps={{
                    input: {
                      sx: { textAlign: 'center', backgroundColor: '#fce4ec', fontWeight: 'bold' },
                      style: { textAlign: 'center' }
                    },
                    htmlInput: { style: { textAlign: 'center' } }
                  }}
                />
              </Grid>
            )}
            {/* ────── BLOQUE CUOTAS DE CRÉDITO ────── */}
            {esCredito() && (
              <Grid item xs={12} sx={{ width: '55em', minWidth: '55em' }}>
                <Paper variant="outlined" sx={{ p: 2, mt: 1, borderColor: '#ff9800', borderRadius: 2, bgcolor: '#fffbf0' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#ff6f00', textAlign: 'center' }}>
                    📅 Cuotas de pago (Crédito - {obtenerDiasCredito()} días)
                  </Typography>
                  
                  {/* SECCIÓN CENTRADA: Cuotas automáticas */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2, p: 1.5, bgcolor: '#fff5e1', borderRadius: 1, border: '1px solid #ffb74d' }}>
                    <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 'bold' }}>Generar cuotas automáticas:</Typography>
                    <Grid container spacing={1} sx={{ maxWidth: '550px', justifyContent: 'center' }}>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          label="Dividir en cuotas"
                          type="number"
                          size="small"
                          fullWidth
                          value={numeroCuotas}
                          onChange={e => {
                            const val = Math.max(0, parseInt(e.target.value) || 0);
                            setNumeroCuotas(val);
                            if (val > 0) {
                              calcularCuotasOpcionA(val);
                            } else {
                              setCuotas([]);
                              setForm(f => ({ ...f, cuotas_json: '' }));
                            }
                          }}
                          inputProps={{ min: 0, max: 12 }}
                          helperText="0 = modo manual"
                        />
                      </Grid>
                      <Grid item xs={12} sm={7}>
                        <Alert severity="info" sx={{ py: 1, height: '56px', display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2">
                            <strong>Vencimiento base:</strong> {new Date(new Date(form.fecha_emision).getTime() + obtenerDiasCredito() * 24 * 60 * 60 * 1000).toISOString().substring(0, 10)}
                          </Typography>
                        </Alert>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Tabla de cuotas */}
                  {cuotas.length > 0 && (
                    <TableContainer component={Paper} sx={{ mb: 2, bgcolor: '#fff' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#fff3e0' }}>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Cuota</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Fecha vencimiento</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Monto (S/)</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Días</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cuotas.map((c, idx) => {
                            const hoy = new Date();
                            const venc = new Date(c.fecha);
                            const diasDiff = Math.floor((venc - hoy) / (1000 * 60 * 60 * 24));
                            return (
                              <TableRow key={idx}>
                                <TableCell align="center">#{c.numero}</TableCell>
                                <TableCell align="center">{c.fecha}</TableCell>
                                <TableCell align="right">{Number(c.monto).toFixed(2)}</TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={diasDiff > 0 ? `+${diasDiff}d` : `${diasDiff}d`}
                                    size="small"
                                    color={diasDiff > 0 ? 'success' : 'error'}
                                    variant="outlined"
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                            <TableCell colSpan={2} align="right">TOTAL:</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {cuotas.reduce((sum, c) => sum + Number(c.monto), 0).toFixed(2)}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* Agregar cuota manual */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1.5, bgcolor: '#fff9c4', borderRadius: 1, borderLeft: '4px solid #ff9800' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>Agregar cuota manual:</Typography>
                    <Grid container spacing={1} alignItems="flex-end" sx={{ maxWidth: '550px', justifyContent: 'center' }}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Fecha vencimiento"
                          type="date"
                          size="small"
                          fullWidth
                          value={fechaCuota}
                          onChange={e => setFechaCuota(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Monto (S/)"
                          type="number"
                          size="small"
                          fullWidth
                          value={montoCuota}
                          onChange={e => setMontoCuota(Number(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          onClick={agregarCuotaManual}
                          size="small"
                          sx={{ height: '40px' }}
                        >
                          Agregar
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>
            )}
            {/* ────────── SWITCH OPCIONES FINANCIERAS ────────── */}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={mostrarFinanciero} onChange={() => setMostrarFinanciero(v => !v)} color="warning" />}
                label={mostrarFinanciero ? "Opciones financieras: Activo" : "Opciones financieras: Oculto"}
                sx={{ mb: 1 }}
              />
            </Grid>

            {mostrarFinanciero && (
            <>
            {/* ── BLOQUE FINANCIERO: Anticipo / Detracción / Retención ── */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, mt: 1, borderColor: '#1976d2', borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, color: '#1976d2' }}>
                  Cargos y descuentos financieros
                </Typography>
                <Grid container spacing={2} alignItems="center">

                  {/* ── Anticipo ── */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!form.es_anticipo}
                          onChange={e => setForm(f => ({ ...f, es_anticipo: e.target.checked, anticipo_referencia_id: null, total_anticipos: 0 }))}
                          color="warning"
                        />
                      }
                      label={<Typography fontWeight="bold">Este comprobante ES un anticipo (no descuenta ninguno)</Typography>}
                    />
                  </Grid>

                  {!form.es_anticipo && (
                    <>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          label="Anticipo de referencia"
                          value={anticipNombre}
                          size="small"
                          fullWidth
                          placeholder="Sin anticipo aplicado"
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <>
                                <Button size="small" onClick={() => setOpenAnticipoSearch(true)} sx={{ whiteSpace: 'nowrap', ml: 1 }}>Buscar</Button>
                                {anticipNombre && (
                                  <Button size="small" color="error" onClick={() => { setAnticipNombre(''); setForm(f => ({ ...f, anticipo_referencia_id: null, total_anticipos: 0 })); }}>✕</Button>
                                )}
                              </>
                            )
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Monto anticipo a deducir"
                          type="number"
                          size="small"
                          fullWidth
                          value={form.total_anticipos}
                          onChange={e => setForm(f => ({ ...f, total_anticipos: parseFloat(e.target.value) || 0 }))}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Total menos anticipo"
                          size="small"
                          fullWidth
                          disabled
                          value={(Number(form.total) - Number(form.total_anticipos || 0)).toFixed(2)}
                          slotProps={{ input: { sx: { backgroundColor: '#fff3e0', fontWeight: 'bold' } } }}
                        />
                      </Grid>
                    </>
                  )}

                  {/* ── Detracción ── */}
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!form.detraccion_aplicada}
                          onChange={e => setForm(f => ({
                            ...f,
                            detraccion_aplicada: e.target.checked,
                            detraccion_monto: e.target.checked ? Math.round(Number(f.total) * 0.12 * 100) / 100 : 0,
                            detraccion_cuenta: '',
                            detraccion_porcentaje_personalizado: false,
                            detraccion_porcentaje: 12
                          }))}
                          color="info"
                        />
                      }
                      label={<Typography fontWeight="bold">Detracción</Typography>}
                    />
                  </Grid>
                  {form.detraccion_aplicada && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Cuenta SUNAT"
                          name="detraccion_cuenta"
                          size="small"
                          fullWidth
                          value={form.detraccion_cuenta}
                          onChange={handleFormChange}
                          placeholder="N° cuenta detracción"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Monto detracción"
                          name="detraccion_monto"
                          type="number"
                          size="small"
                          fullWidth
                          disabled={!!form.detraccion_porcentaje_personalizado}
                          value={form.detraccion_monto}
                          onChange={handleFormChange}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!form.detraccion_porcentaje_personalizado}
                              onChange={e => {
                                const checked = e.target.checked;
                                setForm(f => {
                                  const pct = Number(f.detraccion_porcentaje || 12);
                                  return {
                                    ...f,
                                    detraccion_porcentaje_personalizado: checked,
                                    detraccion_porcentaje: pct,
                                    detraccion_monto: checked ? Math.round(Number(f.total) * pct) / 100 : f.detraccion_monto
                                  };
                                });
                              }}
                            />
                          }
                          label="Personalizar % detracción"
                        />
                      </Grid>
                      {form.detraccion_porcentaje_personalizado && (
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="% Detracción"
                            name="detraccion_porcentaje"
                            type="number"
                            size="small"
                            fullWidth
                            value={form.detraccion_porcentaje}
                            onChange={e => {
                              const pct = Number(e.target.value);
                              setForm(f => ({
                                ...f,
                                detraccion_porcentaje: e.target.value,
                                detraccion_monto: Math.round(Number(f.total) * (Number.isFinite(pct) ? pct : 0)) / 100
                              }));
                            }}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                          />
                        </Grid>
                      )}
                    </>
                  )}

                  {/* ── Retención ── */}
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!form.retencion_aplicada}
                          onChange={e => setForm(f => ({ ...f, retencion_aplicada: e.target.checked, retencion_monto: e.target.checked ? Math.round(Number(f.total) * 0.03 * 100) / 100 : 0 }))}
                          color="secondary"
                        />
                      }
                      label={<Typography fontWeight="bold">Retención</Typography>}
                    />
                  </Grid>
                  {form.retencion_aplicada && (
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Monto retención"
                        name="retencion_monto"
                        type="number"
                        size="small"
                        fullWidth
                        value={form.retencion_monto}
                        onChange={handleFormChange}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                  )}

                  {/* ── Importe neto final ── */}
                  {(form.detraccion_aplicada || form.retencion_aplicada || (!form.es_anticipo && Number(form.total_anticipos) > 0)) && (
                    <Grid item xs={12} sm={5}>
                      <TextField
                        label="Importe neto final (a cobrar)"
                        size="small"
                        fullWidth
                        disabled
                        value={(
                          Number(form.total)
                          - Number(!form.es_anticipo ? (form.total_anticipos || 0) : 0)
                          - Number(form.detraccion_aplicada ? (form.detraccion_monto || 0) : 0)
                          - Number(form.retencion_aplicada ? (form.retencion_monto || 0) : 0)
                        ).toFixed(2)}
                        slotProps={{ input: { sx: { backgroundColor: '#e8f5e9', fontWeight: 'bold', fontSize: 16 } } }}
                      />
                    </Grid>
                  )}

                </Grid>
              </Paper>
            </Grid>
            </>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={mostrarResumenIGV} onChange={() => setMostrarResumenIGV(v => !v)} color="primary" />}
                label={mostrarResumenIGV ? "Resumen por tipo de IGV: Activo" : "Resumen por tipo de IGV: Oculto"}
              />
            </Grid>
            {mostrarResumenIGV && (
              <Grid item xs={12} sx={{ width: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ width: '100%', textAlign: 'center' }}>Resumen por tipo de IGV</Typography>
                <TableContainer component={Paper} sx={{ width: '100%' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo IGV</TableCell>
                        <TableCell align="right">Base Imponible</TableCell>
                        <TableCell align="right">IGV</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        // Agrupar detalles por código IGV y mostrar descripción
                        const codigosGratuitos = ['21','31','32','33','34','35'];
                        const resumen = {};
                        form.detalles.forEach(d => {
                          const tipoIGV = Array.isArray(tiposIGV) ? tiposIGV.find(t => t.codigo === d.tipo_igv) : undefined;
                          const codigo = tipoIGV?.codigo || d.tipo_igv || 'Otro';
                          const descripcion = tipoIGV?.descripcion || d.tipo_igv || 'Otro';
                          if (!resumen[codigo]) resumen[codigo] = { descripcion, base: 0, igv: 0, total: 0, esGratuito: codigosGratuitos.includes(codigo) };
                          if (codigosGratuitos.includes(codigo)) {
                            resumen[codigo].base += parseFloat(d.subtotal) || 0;
                            resumen[codigo].igv += 0;
                            resumen[codigo].total += 0;
                          } else {
                            resumen[codigo].base += parseFloat(d.subtotal) || 0;
                            resumen[codigo].igv += parseFloat(d.igv_monto) || 0;
                            resumen[codigo].total += parseFloat(d.total) || 0;
                          }
                        });
                        // Mostrar solo los tipos con base > 0
                        const filas = Object.entries(resumen).filter(([_, v]) => v.base > 0);
                        // Calcular totales generales, excluyendo gratuitos del total general
                        const totalBase = filas.reduce((a, [_, v]) => v.esGratuito ? a : a + v.base, 0);
                        const totalIGV = filas.reduce((a, [_, v]) => v.esGratuito ? a : a + v.igv, 0);
                        const totalTotal = filas.reduce((a, [_, v]) => v.esGratuito ? a : a + v.total, 0);
                        const monedaSim = form.moneda === 'USD' ? '$ ' : form.moneda === 'EUR' ? '€ ' : 'S/ ';
                        const fmtV = (n) => monedaSim + Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        return <>
                          {filas.map(([codigo, v]) => (
                            <TableRow key={codigo}>
                              <TableCell>{v.descripcion} ({codigo})</TableCell>
                              <TableCell align="right">{fmtV(v.base)}</TableCell>
                              <TableCell align="right">{fmtV(v.igv)}</TableCell>
                              <TableCell align="right">{fmtV(v.total)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                            <TableCell><b>Totales</b></TableCell>
                            <TableCell align="right"><b>{fmtV(totalBase)}</b></TableCell>
                            <TableCell align="right"><b>{fmtV(totalIGV)}</b></TableCell>
                            <TableCell align="right"><b>{fmtV(totalTotal)}</b></TableCell>
                          </TableRow>
                        </>;
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 1 }, gap: 1 }}>
          {!isMobile && <Button onClick={handleClose} startIcon={<Cancel />} color="error" variant="contained">Cancelar</Button>}
          <Button onClick={handleSubmit} disabled={guardando} variant="contained" color="primary" startIcon={<CloudUpload />} fullWidth={isMobile}>{guardando ? 'Guardando...' : 'Guardar Factura'}</Button>
        </DialogActions>
      </Dialog>
    {/* Popup de datos adicionales */}
    <Dialog open={openDatosAdicionales} onClose={handleCloseDatosAdicionales} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Datos adicionales</DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
          Guías de Remisión vinculadas
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
          <TextField
            size="small"
            placeholder="Ej: T001-00000001"
            value={guiaInput}
            onChange={e => setGuiaInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                const g = guiaInput.trim().toUpperCase();
                if (g && !guiasReferencia.includes(g)) setGuiasReferencia(prev => [...prev, g]);
                setGuiaInput('');
              }
            }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const g = guiaInput.trim().toUpperCase();
              if (g && !guiasReferencia.includes(g)) setGuiasReferencia(prev => [...prev, g]);
              setGuiaInput('');
            }}
            disabled={!guiaInput.trim()}
          >Agregar</Button>
        </Box>
        {guiasReferencia.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {guiasReferencia.map((g, i) => (
              <Chip key={i} label={g} size="small" onDelete={() => setGuiasReferencia(prev => prev.filter((_, idx) => idx !== i))} />
            ))}
          </Box>
        )}
        <TextField
          label="Orden de Compra"
          value={ordenCompraAdicional}
          onChange={e => setOrdenCompraAdicional(e.target.value)}
          fullWidth
          margin="dense"
          placeholder="Nro. orden de compra del cliente..."
        />
        <TextField
          label="Placa"
          value={placaAdicional}
          onChange={e => setPlacaAdicional(e.target.value)}
          fullWidth
          margin="dense"
          placeholder="Ingrese la placa..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDatosAdicionales} color="error" variant="contained" startIcon={<Cancel />}>Cerrar</Button>
      </DialogActions>
    </Dialog>
    {/* Popup de cotizaciones */}
    <Dialog open={openCotizaciones} onClose={() => setOpenCotizaciones(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 'bold', fontSize: 20 }}>
            {tipoReferencia === 'cotizacion' ? 'Importar Cotizaciones' : tipoReferencia === 'nota_venta' ? 'Importar Notas de Venta' : 'Importar Guías de Remisión'}
        </DialogTitle>
        <DialogContent>
            <Tabs value={refTab} onChange={(_e, v) => { setRefTab(v); setCotizacionesPage(0); setCotizacionesSeleccionadas([]); }} sx={{ mt: 1, minHeight: 36 }}>
                <Tab label="Disponibles" sx={{ minHeight: 36 }} />
                <Tab label="Ya referenciado" sx={{ minHeight: 36 }} />
            </Tabs>
            {refTab === 1 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Estos documentos (o alguna de sus líneas) ya fueron jalados antes. Puedes volver a jalarlos si quieres facturar lo que falte — revisa primero con el botón <InfoIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} /> qué los referenció.
              </Alert>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, mt:2 }}>
                <TextField
                    placeholder="Buscar por serie, número, RUC, razón social, fecha, estado..."
                    value={filtroCotizacionInput}
                    onChange={e => setFiltroCotizacionInput(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 220 }}
                />
                <TextField
                    size="small"
                    label="Mostrar"
                    type="number"
                    value={cotizacionesRowsPerPage}
                    onChange={e => setCotizacionesRowsPerPage(Number(e.target.value))}
                    sx={{ width: 100 }}
                    inputProps={{ min: 1 }}
                />
            </Box>
            <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: 2, mt: 2 }}>
                <Table size="small">
                    <TableHead>
                    <TableRow>
                        <TableCell></TableCell>
                        <TableCell>Serie</TableCell>
                        <TableCell>Número</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Fecha Emisión</TableCell>
                        <TableCell>Vence</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Total</TableCell>
                        {refTab === 1 && <TableCell>Referencia</TableCell>}
                        <TableCell>Acciones</TableCell>
                    </TableRow>
                    </TableHead>
                    <TableBody>
                    {cotizacionesDisponibles.length === 0 && (
                        <TableRow><TableCell colSpan={refTab === 1 ? 9 : 8} align="center">
                          {refTab === 1 ? 'No hay documentos ya referenciados.' : 'No hay documentos disponibles.'}
                        </TableCell></TableRow>
                    )}
                    {cotizacionesDisponibles.map(cot => (
                        <TableRow key={cot.id}>
                        <TableCell>
                            <Checkbox
                            checked={cotizacionesSeleccionadas.includes(cot.id)}
                            onChange={e => {
                                if (e.target.checked) {
                                setCotizacionesSeleccionadas(arr => [...arr, cot.id]);
                                } else {
                                setCotizacionesSeleccionadas(arr => arr.filter(id => id !== cot.id));
                                }
                            }}
                            />
                        </TableCell>
                        <TableCell>{cot.serie}</TableCell>
                        <TableCell>{cot.numero}</TableCell>
                        <TableCell>{clientes.find(x => x.id === cot.cliente_id)?.razon_social || cot.cliente_id}</TableCell>
                        <TableCell>{cot.fecha_emision?.substring(0, 10)}</TableCell>
                        <TableCell>{cot.fecha_validez?.substring(0, 10)}</TableCell>
                        <TableCell>{cot.estado}</TableCell>
                        <TableCell>{cot.total}</TableCell>
                        {refTab === 1 && (
                          <TableCell>
                            <IconButton
                              size="small"
                              title="Ver quién lo referenció (F1)"
                              tabIndex={0}
                              onClick={(e) => { setRefInfoAnchor(e.currentTarget); setRefInfoTexto(cot._referenciasResumen || 'Sin detalle.'); }}
                              onKeyDown={(e) => { if (e.key === 'F1') { e.preventDefault(); setRefInfoAnchor(e.currentTarget); setRefInfoTexto(cot._referenciasResumen || 'Sin detalle.'); } }}
                            >
                              <InfoIcon fontSize="small" color="primary" />
                            </IconButton>
                          </TableCell>
                        )}
                        <TableCell>
                            <Button size="small" onClick={() => setVistaCotizacion(cot)}>Vista previa</Button>
                        </TableCell>
                        </TableRow>
                    ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Popover
              open={!!refInfoAnchor}
              anchorEl={refInfoAnchor}
              onClose={() => setRefInfoAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box sx={{ p: 2, maxWidth: 320 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Ya referenciado por:</Typography>
                <Typography variant="body2">{refInfoTexto}</Typography>
              </Box>
            </Popover>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" sx={{ ml: 1 }}>
                    {cotizacionesDisponibles.length === 0
                    ? 'Mostrando 0-0 de 0 cotizaciones'
                    : `Mostrando ${cotizacionesPage * cotizacionesRowsPerPage + 1}-${Math.min((cotizacionesPage + 1) * cotizacionesRowsPerPage, cotizacionesDisponibles.length)} de ${cotizacionesDisponibles.length} cotizaciones`}
                </Typography>
                <Box>
                    <Button disabled={cotizacionesPage === 0} onClick={() => setCotizacionesPage(cotizacionesPage - 1)}>Anterior</Button>
                    <Button
                    disabled={cotizacionesRowsPerPage === -1 ? true : (cotizacionesPage + 1) * cotizacionesRowsPerPage >= cotizacionesDisponibles.length}
                    onClick={() => setCotizacionesPage(cotizacionesPage + 1)}
                    >Siguiente</Button>
                </Box>
            </Box>
            {vistaCotizacion && (
                <Dialog open={true} onClose={() => setVistaCotizacion(null)} maxWidth="xl" fullWidth>
                    <DialogTitle sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 'bold', fontSize: 20 }}>
                    Vista previa de Cotización
                    </DialogTitle>
                    <DialogContent>
                    <Box sx={{ mb: 2, p: 2, borderRadius: 2, background: '#f5f5f5', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Cliente:</Typography>
                            <Typography>{clientes.find(x => x.id === vistaCotizacion.cliente_id)?.razon_social || ''}</Typography>
                            <Typography variant="body2" color="text.secondary">RUC: {clientes.find(x => x.id === vistaCotizacion.cliente_id)?.numero_documento || ''}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Documento:</Typography>
                            <Typography>Serie: {vistaCotizacion.serie} Número: {vistaCotizacion.numero}</Typography>
                            <Typography variant="body2" color="text.secondary">Fecha Emisión: {vistaCotizacion.fecha_emision?.substring(0, 10) || ''}</Typography>
                            <Typography variant="body2" color="text.secondary">Vence: {vistaCotizacion.fecha_validez?.substring(0, 10) || ''}</Typography>
                            <Typography variant="body2" color="text.secondary">Moneda: {vistaCotizacion.moneda}</Typography>
                            <Typography variant="body2" color="text.secondary">Estado: {vistaCotizacion.estado}</Typography>
                        </Grid>
                        </Grid>
                        <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Detalle de productos:</Typography>
                        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
                            <Table size="small">
                            <TableHead>
                                <TableRow>
                                <TableCell>Código</TableCell>
                                <TableCell>Producto</TableCell>
                                <TableCell>Almacén</TableCell>
                                <TableCell>Cantidad</TableCell>
                                <TableCell>U.Medida</TableCell>
                                <TableCell>Precio</TableCell>
                                <TableCell>Subtotal</TableCell>
                                <TableCell>IGV</TableCell>
                                <TableCell>Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(Array.isArray(vistaCotizacion.detalles) ? vistaCotizacion.detalles : (vistaCotizacion.detalles ? JSON.parse(vistaCotizacion.detalles) : [])).map((d, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{d.codigo_producto || productos.find(p => p.id === d.producto_id)?.codigo || ''}</TableCell>
                                    <TableCell>{d.descripcion_producto || productos.find(p => p.id === d.producto_id)?.nombre || ''}</TableCell>
                                    <TableCell>{almacenes.find(a => Number(a.id) === Number(d.almacen_id))?.nombre || ''}</TableCell>
                                    <TableCell>{d.cantidad}</TableCell>
                                    <TableCell>{d.unidad_medida}</TableCell>
                                    <TableCell>{d.precio_unitario}</TableCell>
                                    <TableCell>{d.subtotal}</TableCell>
                                    <TableCell>{d.igv_monto}</TableCell>
                                    <TableCell>{d.total}</TableCell>
                                </TableRow>
                                ))}
                            </TableBody>
                            </Table>
                        </TableContainer>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Observaciones:</Typography>
                        <Typography>{vistaCotizacion.observaciones || '-'}</Typography>
                        </Box>
                    </Box>
                    </DialogContent>
                    <DialogActions>
                    <Button onClick={() => setVistaCotizacion(null)} color="error" variant="contained">Cerrar</Button>
                    </DialogActions>
                </Dialog>
                )}
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpenCotizaciones(false)} startIcon={<Cancel />} color="error" variant="contained">Cancelar</Button>
            <Button onClick={jalarCotizacionesSeleccionadas} variant="contained" color="primary" startIcon={<CheckCircle />}>
                Jalar seleccionadas
            </Button>
        </DialogActions>
    </Dialog>
    {/* Búsqueda de comprobante anticipo de referencia */}
    <Dialog open={openAnticipoSearch} onClose={() => setOpenAnticipoSearch(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#f57c00', color: '#fff', fontWeight: 'bold', fontSize: 20 }}>
        Seleccionar comprobante de anticipo
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, mb: 2 }}>
          <TextField
            placeholder="Buscar por serie, número o cliente..."
            value={filtroAnticipo}
            onChange={e => setFiltroAnticipo(e.target.value)}
            size="small"
            fullWidth
          />
        </Box>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Serie</TableCell>
                <TableCell>Número</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Seleccionar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {anticiposDisponibles.map(a => (
                <TableRow key={a.id}>
                  <TableCell>{a.serie}</TableCell>
                  <TableCell>{a.numero}</TableCell>
                  <TableCell>{clientes.find(x => x.id === a.cliente_id)?.razon_social || a.cliente_id}</TableCell>
                  <TableCell>{a.fecha_emision?.substring(0, 10)}</TableCell>
                  <TableCell align="right">{Number(a.total).toFixed(2)}</TableCell>
                  <TableCell align="center">
                    <Button size="small" variant="contained" color="warning" onClick={() => {
                      setForm(f => ({ ...f, anticipo_referencia_id: a.id, total_anticipos: Number(a.total) || 0 }));
                      setAnticipNombre(`${a.serie}-${a.numero}`);
                      setOpenAnticipoSearch(false);
                    }}>
                      Seleccionar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {anticiposDisponibles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No se encontraron comprobantes marcados como anticipo
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenAnticipoSearch(false)} color="error" variant="contained" startIcon={<Cancel />}>Cerrar</Button>
      </DialogActions>
    </Dialog>

    {/* 🔨 DIÁLOGO DE ANULACIÓN ELECTRÓNICA */}
    <Dialog open={openAnulacion} onClose={() => setOpenAnulacion(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: '#c62828', color: '#fff', fontWeight: 'bold', fontSize: 18 }}>
        Anular Comprobante Electrónicamente
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
          ⚠️ <strong>Atención:</strong> La anulación electrónica se reportará a SUNAT. Este proceso NO puede deshacerse.
        </Alert>
        {facturaAAnular && (
          <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography><strong>Comprobante:</strong> {facturaAAnular.serie}-{String(facturaAAnular.numero).padStart(8, '0')}</Typography>
            <Typography><strong>Cliente:</strong> {clientes.find(c => c.id === facturaAAnular.cliente_id)?.razon_social || 'N/A'}</Typography>
            <Typography><strong>Total:</strong> S/ {Number(facturaAAnular.total).toFixed(2)}</Typography>
            <Typography><strong>Fecha Emisión:</strong> {facturaAAnular.fecha_emision?.substring(0, 10)}</Typography>
            <Typography sx={{ mt: 1, color: '#d32f2f', fontWeight: 'bold' }}>
              Estado CPE: {facturaAAnular.estado_cpe}
            </Typography>
            {facturaAAnular.ultimo_estado_anulacion && (
              <Typography sx={{ mt: 0.5, color: '#ef6c00', fontWeight: 'bold' }}>
                Estado anulación: {facturaAAnular.ultimo_estado_anulacion}
              </Typography>
            )}
          </Box>
        )}
        <TextField
          label="Motivo de Anulación (6-120 caracteres)"
          value={motivoAnulacion}
          onChange={e => setMotivoAnulacion(e.target.value.substring(0, 120))}
          fullWidth
          multiline
          rows={3}
          margin="normal"
          placeholder="Ej: Anulación por solicitud del cliente, error en datos, etc."
          error={motivoAnulacion.length < 6 && motivoAnulacion.length > 0}
          helperText={motivoAnulacion.length < 6 && motivoAnulacion.length > 0 ? 'Mínimo 6 caracteres' : `${motivoAnulacion.length}/120`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenAnulacion(false)} color="error" variant="outlined" startIcon={<Cancel />} disabled={procesandoAnulacion}>
          Cancelar
        </Button>
        <Button
          onClick={async () => {
            if (procesandoAnulacion) return;
            if (motivoAnulacion.trim().length < 6) {
              alert('El motivo debe tener mínimo 6 caracteres');
              return;
            }
            setProcesandoAnulacion(true);
            try {
              const response = await axios.post(`/api/comprobante/${facturaAAnular.id}/anular`, {
                motivo: motivoAnulacion,
                usuario: usuarioLogueado?.username || usuarioLogueado?.email || 'sistema'
              });
              const data = response?.data || {};
              const ticketTxt = data.ticket ? `\nTicket SUNAT: ${data.ticket}` : '';
              alert(`✓ Anulación enviada a SUNAT.${ticketTxt}\nEstado: ${data.estado_anulacion || 'ACEPTADA'}`);
              setOpenAnulacion(false);
              setFacturaAAnular(null);
              setMotivoAnulacion('');
              fetchFacturas();
            } catch (err) {
              const errorMsg = err.response?.data?.error || err.message;
              const validationErrors = err.response?.data?.validationErrors || [];
              if (validationErrors.length > 0) {
                alert('Errores de validación:\n• ' + validationErrors.join('\n• '));
              } else {
                alert('Error al procesar anulación: ' + errorMsg);
              }
            } finally {
              setProcesandoAnulacion(false);
            }
          }}
          color="error"
          variant="contained"
          startIcon={<Gavel />}
          disabled={motivoAnulacion.trim().length < 6 || procesandoAnulacion}
        >
          {procesandoAnulacion ? 'Procesando…' : 'Proceder con Anulación'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={openPdfDialog} onClose={cerrarDialogoPdf} maxWidth="sm" fullWidth>
      <DialogTitle>Descargar PDF</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
          Selecciona el formato de impresión. La descarga se hace directamente desde el backend y el formato elegido puede quedar como predeterminado.
        </Alert>
        
        {pdfResolucionCargando ? (
          <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">Resolviendo plantilla...</Typography>
          </Box>
        ) : pdfResolucion && pdfResolucion.plantilla_nombre ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2"><strong>Plantilla:</strong> {pdfResolucion.plantilla_nombre}</Typography>
            <Typography variant="body2"><strong>Ámbito:</strong> {pdfResolucion.scope === 'Global' ? 'Global (todas las sucursales)' : pdfResolucion.scope === 'Specific' ? 'Específica para esta sucursal' : 'Heredada de global'}</Typography>
            {pdfResolucion.is_inherited && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#1976d2' }}>
                ℹ️ Esta sucursal hereda la asignación de la configuración global
              </Typography>
            )}
          </Alert>
        ) : null}
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
          {[
            { codigo: 'A4', titulo: 'A4', detalle: 'Formato completo' },
            { codigo: 'A5', titulo: 'A5', detalle: 'Formato compacto' },
            { codigo: 'T80', titulo: 'Ticket 80mm', detalle: 'Impresora térmica' }
          ].map((item) => (
            <Paper
              key={item.codigo}
              onClick={() => {
                setPdfFormatoSeleccionado(item.codigo);
                actualizarResolucionFormato(item.codigo);
              }}
              sx={{
                p: 2,
                cursor: 'pointer',
                border: '2px solid',
                borderColor: pdfFormatoSeleccionado === item.codigo ? '#1976d2' : '#d0d7de',
                backgroundColor: pdfFormatoSeleccionado === item.codigo ? '#e3f2fd' : '#fff'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{item.titulo}</Typography>
              <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>{item.detalle}</Typography>
              {localStorage.getItem('factura_pdf_formato_default') === item.codigo && (
                <Chip label="Predeterminado" color="primary" size="small" sx={{ mt: 1, display: 'flex', mx: 'auto', width: 'fit-content' }} />
              )}
            </Paper>
          ))}
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={guardarPdfComoPredeterminado}
              onChange={(e) => setGuardarPdfComoPredeterminado(e.target.checked)}
              color="primary"
            />
          }
          label={`Guardar ${pdfFormatoSeleccionado} como formato predeterminado`}
        />
        {facturaPdfTarget && (
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Documento: {facturaPdfTarget.serie}-{String(facturaPdfTarget.numero || '').padStart(8, '0')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={cerrarDialogoPdf} disabled={pdfDescargando}>Cancelar</Button>
        <Button onClick={descargarPdfFactura} variant="contained" disabled={pdfDescargando}>
          {pdfDescargando ? 'Descargando...' : `Descargar ${pdfFormatoSeleccionado}`}
        </Button>
      </DialogActions>
    </Dialog>

    <ClienteRapidoDialog
      open={openNuevoCliente}
      onClose={() => setOpenNuevoCliente(false)}
      onClienteCreado={(cliente) => {
        debouncedFetchClientes.cancel();
        ++fetchClienteGenRef.current;
        setClientes(prev => [cliente, ...prev]);
        setForm(f => ({ ...f, cliente_id: cliente.id }));
        skipClienteSearch.current = true;
        setClienteInput(cliente.razon_social || '');
        setOpenNuevoCliente(false);
      }}
      initialQuery={clienteInput}
    />
    <ProductoRapidoDialog
      open={openNuevoProducto}
      onClose={() => setOpenNuevoProducto(false)}
      onProductoCreado={(producto) => {
        setProductos(prev => [producto, ...prev]);
        setDetalle(d => ({
          ...d,
          producto_id: producto.id,
          unidad_medida: producto.unidad_medida_id || d.unidad_medida,
          precio_unitario: producto.precio != null ? producto.precio : d.precio_unitario
        }));
        setProductoInput(producto?.nombre || '');
        setOpenNuevoProducto(false);
      }}
      initialNombre={productoInput}
    />
    <ProductoBuscadorDialog
      open={openBuscadorProducto}
      onClose={() => setOpenBuscadorProducto(false)}
      onSelect={handleBuscadorSelect}
    />
    <StockAlmacenesDialog
      open={openStockAlmacen}
      onClose={() => setOpenStockAlmacen(false)}
      productoId={detalle.producto_id}
      productoNombre={detalle.descripcion_producto}
    />

    {/* ── Dialog: correlativo duplicado ─────────────────────────────── */}
    <Dialog open={!!correlativoError} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.dark' }}>
        <Warning color="warning" /> Número de correlativo duplicado
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {correlativoError?.error}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ¿Desea regularizar automáticamente la serie{' '}
          <strong>{correlativoError?.serie}</strong> al número{' '}
          <strong>{correlativoError?.numero_siguiente}</strong> y volver a intentar el guardado?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCorrelativoError(null)}>No, cancelar</Button>
        <Button
          variant="contained" color="warning"
          onClick={handleFixCorrelativoFactura}
          disabled={fixingCorrelativo}
        >
          {fixingCorrelativo ? 'Regularizando...' : 'Sí, regularizar serie'}
        </Button>
      </DialogActions>
    </Dialog>

    </Box>
    <WhatsAppDialog
      open={wspDialog.open}
      onClose={() => setWspDialog({ open: false, data: null })}
      docTipo="comprobante"
      tipoDoc="01"
      docId={wspDialog.data?.id}
      clienteId={wspDialog.data?.cliente_id}
      serie={wspDialog.data?.serie || ''}
      numero={wspDialog.data?.numero || ''}
      fecha={wspDialog.data?.fecha_emision || ''}
      total={wspDialog.data?.total || 0}
      moneda={wspDialog.data?.moneda || 'PEN'}
      clienteNombre={clientes.find(x => x.id === wspDialog.data?.cliente_id)?.razon_social || ''}
    />
    <EmailDialog
      open={emailDialog.open}
      onClose={() => setEmailDialog({ open: false, data: null })}
      docTipo="comprobante"
      tipoDoc="01"
      docId={emailDialog.data?.id}
      clienteId={emailDialog.data?.cliente_id}
      serie={emailDialog.data?.serie || ''}
      numero={emailDialog.data?.numero || ''}
      fecha={emailDialog.data?.fecha_emision || ''}
      total={emailDialog.data?.total || 0}
      moneda={emailDialog.data?.moneda || 'PEN'}
      clienteNombre={clientes.find(x => x.id === emailDialog.data?.cliente_id)?.razon_social || ''}
    />
</Box>
  );
};
export default FacturaPage;
